import { useCallback, useEffect, useMemo, useRef, useState, type KeyboardEvent as ReactKeyboardEvent, type ReactNode } from 'react'
import { Navigate, useLocation, useNavigate, useParams } from 'react-router-dom'
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import logo from '../traderhub24-logo.svg'
import { useAuth } from './contexts/AuthContext'
import { supabase } from './lib/supabase'
import { calcClosedTradesMetrics } from './lib/tradeCalculations'
import { useMarketDataRefreshInterval } from './hooks/useMarketDataRefreshInterval'
import QuickAddTradeModal from './components/QuickAddTradeModal'
import ImportTrades from './components/ImportTrades'
import TradeListQuickActions from './components/TradeListQuickActions'
import TradeQuickReviewModal from './components/TradeQuickReviewModal'
import TradeDetail from './TradeDetail'
import type { Trade } from './types/trade'
import { useMembership } from './hooks/useMembership'
import { useTranslation } from 'react-i18next'
import { currencies } from './components/CurrencySelector'
import AccountFeeRulesEditor from './components/AccountFeeRulesEditor'
import { useNotification } from './contexts/NotificationContext'
import {
  buildFeeStateFromRules,
  emptyFeeRuleFormMap,
  type AccountFeeRuleForm,
} from './lib/accountFeeRules'
import { fetchAccountFeeConfig, saveAccountFeeRules } from './lib/accountFeeRulesDb'
import { navigateToTradeDetail } from './utils/tradeNavigation'
import DashboardFeed from './components/DashboardFeed'
import SymbolLogo from './components/SymbolLogo'
import Insights from './Insights'
import Tools from './Tools'
import Community from './Community'
import ProcessAnalysisSection from './components/ProcessAnalysisSection'
import { DEFAULT_WIDGET_FILTER, type WidgetFilterSettings, type WidgetMarket } from './lib/tradeCalculations'
import { convertPnl, convertToDisplayCurrency, useDashboardCurrency } from './hooks/useDashboardCurrency'
import './app-test.css'

type RangeKey = '7D' | '4W' | 'YTD' | '1Y' | 'ALL'
type OpenPositionColumnId =
  | 'side'
  | 'opened'
  | 'entry'
  | 'averageCost'
  | 'positionSize'
  | 'totalInvested'
  | 'sl'
  | 'tp'
  | 'risk'
  | 'riskPercent'
  | 'price'
  | 'pnlPercent'
  | 'pnl'
  | 'account'
  | 'exchange'
  | 'tags'
type MetricId =
  | 'openPositions'
  | 'totalPnLPercent'
  | 'totalPnlOpen'
  | 'totalRiskExposure'
  | 'totalPnl'
  | 'totalTrades'
  | 'winRate'
  | 'profitFactor'
  | 'avgPnlPercent'
  | 'weightedPnlPercent'
  | 'expectancy'
  | 'averageWin'
  | 'averageLoss'
  | 'averageRR'

interface TestWidgetFilter {
  strategyIds: string[]
  accountIds: string[]
  markets: string[]
  exchanges: string[]
  tags: string[]
}

type WidgetFilterMap = Record<string, TestWidgetFilter>

const EMPTY_WIDGET_FILTER: TestWidgetFilter = {
  strategyIds: [],
  accountIds: [],
  markets: [],
  exchanges: [],
  tags: [],
}

interface DashboardTrade {
  id: string
  symbol: string
  market?: string | null
  exchange?: string | null
  long_short?: string | null
  entry_date?: string | null
  exit_date?: string | null
  pnl?: number | null
  pnl_percentage?: number | null
  unrealized_pnl?: number | null
  unrealized_pnl_percentage?: number | null
  rr?: number | null
  currency?: string | null
  account_id?: string | null
  strategy_id?: string | null
  entry_price?: number | null
  exit_price?: number | null
  stop_loss?: number | null
  quantity?: number | null
  contract_size?: number | null
  total_entry_amount?: number | null
  total_exit_amount?: number | null
  position_size?: number | null
  cost_basis_remaining?: number | null
  take_profit?: number | null
  current_price?: number | null
  tags?: string[] | null
  fee?: number | null
  time_interval?: string | null
}

interface DashboardAccount {
  id: string
  name: string
  description?: string | null
  platform?: string | null
  currency?: string | null
  initial_balance?: number | null
  is_active?: boolean | null
}

type AccountTxType = 'deposit' | 'withdrawal' | 'buy' | 'sell' | 'fee' | 'adjustment'

interface AccountTransaction {
  id: string
  account_id: string
  type: AccountTxType
  amount: number
  original_amount: number | null
  original_currency: string | null
  fx_rate: number | null
  trade_id: string | null
  occurred_at: string
  note: string | null
  source: 'opening' | 'trade' | 'manual'
  created_at: string
}

interface AccountLedgerRow extends AccountTransaction {
  runningBalance: number
}

interface DashboardStrategy {
  id: string
  display_name: string
  description?: string | null
  is_active?: boolean | null
  is_public?: boolean | null
  created_at?: string | null
}

interface SearchResult {
  id: string
  kind: 'Open' | 'Closed' | 'Account' | 'Strategy'
  title: string
  meta: string
  path: string
  amount?: string
  tone?: 'positive' | 'negative'
  market?: string
  exchange?: string | null
}

interface DashboardMetrics {
  closed: DashboardTrade[]
  open: DashboardTrade[]
  wins: DashboardTrade[]
  losses: DashboardTrade[]
  breakeven: number
  totalPnl: number
  openPnl: number
  winRate: number
  avgRR: number
  profitFactor: number
}

const METRIC_OPTIONS: { id: MetricId; label: string }[] = [
  { id: 'openPositions', label: 'Open Positions' },
  { id: 'totalPnLPercent', label: 'Total P/L % (Open)' },
  { id: 'totalPnlOpen', label: 'Total P/L (Open)' },
  { id: 'totalRiskExposure', label: 'Total Risk Exposure' },
  { id: 'totalPnl', label: 'Total P&L' },
  { id: 'totalTrades', label: 'Total Trades' },
  { id: 'winRate', label: 'Win Rate' },
  { id: 'profitFactor', label: 'Profit Factor' },
  { id: 'avgPnlPercent', label: 'Avg P/L %' },
  { id: 'weightedPnlPercent', label: 'Weighted P/L %' },
  { id: 'expectancy', label: 'Expectancy' },
  { id: 'averageWin', label: 'Average Win' },
  { id: 'averageLoss', label: 'Average Loss' },
  { id: 'averageRR', label: 'Average R:R' },
]

const DEFAULT_PRIMARY_METRICS: MetricId[] = ['totalPnl', 'winRate', 'totalPnlOpen', 'profitFactor']
const DEFAULT_MINI_METRICS: MetricId[] = ['totalTrades', 'averageRR', 'averageWin', 'averageLoss', 'avgPnlPercent', 'openPositions']
const METRIC_IDS = new Set(METRIC_OPTIONS.map((metric) => metric.id))

const OPEN_POSITION_COLUMNS: { id: OpenPositionColumnId; label: string }[] = [
  { id: 'side', label: 'Side' },
  { id: 'opened', label: 'Opened' },
  { id: 'entry', label: 'Entry' },
  { id: 'averageCost', label: 'Average Cost' },
  { id: 'positionSize', label: 'Position Size' },
  { id: 'totalInvested', label: 'Total Invested' },
  { id: 'sl', label: 'Stop Loss' },
  { id: 'tp', label: 'Take Profit' },
  { id: 'risk', label: 'Risk' },
  { id: 'riskPercent', label: 'Risk %' },
  { id: 'price', label: 'Current Price' },
  { id: 'pnlPercent', label: 'P/L %' },
  { id: 'pnl', label: 'Unrealized P/L' },
  { id: 'account', label: 'Account' },
  { id: 'exchange', label: 'Exchange' },
  { id: 'tags', label: 'Tags' },
]

const DEFAULT_OPEN_POSITION_COLUMNS: OpenPositionColumnId[] = ['side', 'opened', 'entry', 'price', 'pnlPercent', 'pnl']

type JournalColumnId =
  | 'instrument'
  | 'market'
  | 'exchange'
  | 'account'
  | 'direction'
  | 'status'
  | 'strategy'
  | 'timeframe'
  | 'entry'
  | 'exit'
  | 'planned_rr'
  | 'realized_rr'
  | 'pl'
  | 'pl_pct'
  | 'total_invested'
  | 'holding_days'

const JOURNAL_TABLE_COLUMNS: { id: JournalColumnId; label: string }[] = [
  { id: 'instrument', label: 'Instrument' },
  { id: 'market', label: 'Market' },
  { id: 'exchange', label: 'Exchange' },
  { id: 'account', label: 'Account' },
  { id: 'direction', label: 'Direction' },
  { id: 'status', label: 'Status' },
  { id: 'strategy', label: 'Strategy' },
  { id: 'timeframe', label: 'Timeframe' },
  { id: 'entry', label: 'Entry' },
  { id: 'exit', label: 'Exit' },
  { id: 'planned_rr', label: 'Planned RR' },
  { id: 'realized_rr', label: 'Realized RR' },
  { id: 'pl', label: 'P/L' },
  { id: 'pl_pct', label: 'P/L %' },
  { id: 'total_invested', label: 'Total Invested' },
  { id: 'holding_days', label: 'Holding (days)' },
]

const DEFAULT_JOURNAL_COLUMNS: JournalColumnId[] = ['instrument', 'market', 'direction', 'entry', 'exit', 'planned_rr', 'status', 'pl']

type OpenPositionSortKey = 'instrument' | OpenPositionColumnId

function loadJournalColumns(): JournalColumnId[] {
  try {
    const stored = JSON.parse(localStorage.getItem('app-test-journal-columns') || '[]')
    const validIds = new Set(JOURNAL_TABLE_COLUMNS.map((column) => column.id))
    if (Array.isArray(stored)) {
      const valid = stored.filter((id): id is JournalColumnId => validIds.has(id))
      if (valid.length) return valid
    }
  } catch {
    // Fall back to defaults when local storage is unavailable or malformed.
  }
  return DEFAULT_JOURNAL_COLUMNS
}

function loadOpenPositionColumns(): OpenPositionColumnId[] {
  try {
    const stored = JSON.parse(localStorage.getItem('app-test-open-position-columns') || '[]')
    const validIds = new Set(OPEN_POSITION_COLUMNS.map((column) => column.id))
    if (Array.isArray(stored)) {
      const valid = stored.filter((id): id is OpenPositionColumnId => validIds.has(id))
      if (valid.length) return valid
    }
  } catch {
    // Use defaults when local storage is unavailable or malformed.
  }
  return DEFAULT_OPEN_POSITION_COLUMNS
}

function loadMetricSelection(key: string, defaults: MetricId[], expectedLength: number): MetricId[] {
  try {
    const stored = JSON.parse(localStorage.getItem(key) || '[]')
    if (Array.isArray(stored) && stored.length === expectedLength && stored.every((id) => METRIC_IDS.has(id))) {
      return stored as MetricId[]
    }
  } catch {
    // Use defaults when local storage is unavailable or malformed.
  }
  return defaults
}

function loadWidgetFilters(): WidgetFilterMap {
  try {
    const stored = JSON.parse(localStorage.getItem('app-test-widget-filters') || '{}')
    if (stored && typeof stored === 'object' && !Array.isArray(stored)) {
      return Object.fromEntries(Object.entries(stored).map(([slotId, rawValue]) => {
        const value = (rawValue || {}) as Partial<TestWidgetFilter> & {
          strategyId?: string
          accountId?: string
          market?: string
          exchange?: string
          tag?: string
        }
        return [slotId, {
          strategyIds: Array.isArray(value.strategyIds) ? value.strategyIds : value.strategyId ? [value.strategyId] : [],
          accountIds: Array.isArray(value.accountIds) ? value.accountIds : value.accountId ? [value.accountId] : [],
          markets: Array.isArray(value.markets) ? value.markets : value.market && value.market !== 'all' ? [value.market] : [],
          exchanges: Array.isArray(value.exchanges) ? value.exchanges : value.exchange && value.exchange !== 'all' ? [value.exchange] : [],
          tags: Array.isArray(value.tags) ? value.tags : value.tag ? [value.tag] : [],
        }]
      }))
    }
  } catch {
    // Use empty filters when local storage is unavailable or malformed.
  }
  return {}
}

function applyTestWidgetFilter(trades: DashboardTrade[], filter: TestWidgetFilter): DashboardTrade[] {
  return trades.filter((trade) => {
    if (filter.strategyIds.length && !filter.strategyIds.includes(trade.strategy_id || '')) return false
    if (filter.accountIds.length && !filter.accountIds.includes(trade.account_id || '')) return false
    if (filter.markets.length && !filter.markets.includes(trade.market || '')) return false
    if (filter.exchanges.length && !filter.exchanges.includes(trade.exchange || '')) return false
    if (filter.tags.length && !filter.tags.some((tag) => (trade.tags || []).includes(tag))) return false
    return true
  })
}

const icons: Record<string, React.ReactNode> = {
  overview: <path d="M4 10.5 12 4l8 6.5V20a1 1 0 0 1-1 1h-5v-6h-4v6H5a1 1 0 0 1-1-1v-9.5Z" />,
  inbox: <><path d="M22 12h-6l-2 3h-4l-2-3H2" /><path d="M5.5 5h13l3.5 7v6a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2v-6L5.5 5Z" /></>,
  trades: <path d="M5 19V9m7 10V5m7 14v-7M3 19h18" />,
  analytics: <path d="M4 18 9 12l4 3 7-9M16 6h4v4" />,
  accounts: <path d="M3 8h18M5 12h14M5 5h14l2 3H3l2-3Zm0 7v7m4-7v7m6-7v7m4-7v7M3 19h18" />,
  strategies: <path d="m13 2-9 12h7l-1 8 10-13h-7V2Z" />,
  insights: <path d="M9 18h6m-5 3h4M8.5 14.5a6 6 0 1 1 7 0c-.9.7-1.5 1.7-1.5 2.5h-4c0-.8-.6-1.8-1.5-2.5Z" />,
  tools: <path d="M14.5 6.5a4 4 0 0 0-5-5L12 4 9 7 6.5 4.5a4 4 0 0 0 5 5L19 17a1.4 1.4 0 0 1-2 2l-7.5-7.5" />,
  community: <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2m7-10a4 4 0 1 0 0-8 4 4 0 0 0 0 8Zm13 10v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />,
  add: <path d="M12 5v14M5 12h14" />,
  search: <><circle cx="11" cy="11" r="7" /><path d="m20 20-4-4" /></>,
  bell: <><path d="M18 8a6 6 0 0 0-12 0c0 7-3 7-3 9h18c0-2-3-2-3-9Z" /><path d="M10 21h4" /></>,
  download: <><path d="M12 3v12m-5-5 5 5 5-5" /><path d="M5 20h14" /></>,
  upload: <><path d="M12 16V4m0 0 5 5M12 4 7 9" /><path d="M5 20h14" /></>,
  filter: <path d="M4 5h16l-6.5 7.2V19l-3 1v-7.8L4 5Z" />,
  columns: <><path d="M4 5h16v14H4z" /><path d="M10 5v14m4-14v14" /></>,
  menu: <path d="M4 7h16M4 12h16M4 17h16" />,
  close: <path d="m6 6 12 12M18 6 6 18" />,
  chevron: <path d="m9 18 6-6-6-6" />,
  arrow: <path d="m5 12 5 5L20 7" />,
  edit: <path d="M11 5H6a2 2 0 0 0-2 2v11a2 2 0 0 0 2 2h11a2 2 0 0 0 2-2v-5m-1.414-9.414a2 2 0 1 1 2.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />,
  trash: <path d="M3 6h18m-2 0-.867 12.142A2 2 0 0 1 14.138 20H9.862a2 2 0 0 1-1.995-1.858L7 6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2m-6 5v6m4-6v6" />,
}

function Icon({ name, size = 18 }: { name: string; size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      {icons[name]}
    </svg>
  )
}

const navSections = [
  {
    label: 'TRADING',
    items: [
      { label: 'Overview', icon: 'overview', path: '/app-test' },
      { label: 'Inbox', icon: 'inbox', path: '/app-test/inbox' },
      { label: 'Trades', icon: 'trades', path: '/app-test/trades' },
      { label: 'Analytics', icon: 'analytics', path: '/app-test/analytics' },
      { label: 'Accounts', icon: 'accounts', path: '/app-test/accounts' },
      { label: 'Strategies', icon: 'strategies', path: '/app-test/strategies' },
    ],
  },
  {
    label: 'WORKSPACE',
    items: [
      { label: 'Insights', icon: 'insights', path: '/app-test/insights' },
      { label: 'Tools', icon: 'tools', path: '/app-test/tools' },
      { label: 'Community', icon: 'community', path: '/app-test/community' },
    ],
  },
]

const formatMoney = (value: number, currency = 'USD', compact = false) => {
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
      maximumFractionDigits: compact ? 0 : 2,
      notation: compact ? 'compact' : 'standard',
    }).format(value)
  } catch {
    return `$${value.toFixed(compact ? 0 : 2)}`
  }
}

const formatDate = (date?: string | null) => {
  if (!date) return 'Open'
  return new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric' }).format(new Date(date))
}

const formatDateTime = (date?: string | null) => {
  if (!date) return '—'
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(date))
}

const TX_TYPE_LABEL: Record<AccountTxType, string> = {
  deposit: 'Deposit',
  withdrawal: 'Withdrawal',
  buy: 'Buy',
  sell: 'Sell',
  fee: 'Fee',
  adjustment: 'Adjustment',
}

type ManualCashType = 'deposit' | 'withdrawal' | 'adjustment'

function isEditableLedgerRow(row: AccountTransaction) {
  if (row.source === 'opening') return true
  if (row.source !== 'manual') return false
  return row.type === 'deposit' || row.type === 'withdrawal' || row.type === 'adjustment'
}

function ledgerCashModalType(row: AccountTransaction): ManualCashType {
  if (row.source === 'opening') return 'deposit'
  if (row.type === 'withdrawal' || row.type === 'adjustment' || row.type === 'deposit') return row.type
  return 'deposit'
}

const MANUAL_CASH_LABEL: Record<ManualCashType, string> = {
  deposit: 'Deposit',
  withdrawal: 'Withdraw',
  adjustment: 'Adjustment',
}

function toDateTimeLocal(value: Date) {
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${value.getFullYear()}-${pad(value.getMonth() + 1)}-${pad(value.getDate())}T${pad(value.getHours())}:${pad(value.getMinutes())}`
}

function convertBetweenCurrencies(
  amount: number,
  fromCurrency: string,
  toCurrency: string,
  pivotCurrency: string,
  latestRates: Map<string, number>,
) {
  if (!amount) return 0
  const from = fromCurrency || pivotCurrency
  const to = toCurrency || pivotCurrency
  if (from === to) return amount
  const inPivot = from === pivotCurrency
    ? amount
    : convertToDisplayCurrency(amount, from, pivotCurrency, latestRates)
  if (to === pivotCurrency) return inPivot
  const toRate = latestRates.get(`${to}:${pivotCurrency}`)
  return toRate ? inPivot / toRate : inPivot
}

function getAccountCashBalance(account: DashboardAccount, cashByAccount: Record<string, number>) {
  if (account.id in cashByAccount) return Number(cashByAccount[account.id])
  return Number(account.initial_balance || 0)
}

function getAccountOpenMarketValue(
  accountId: string,
  trades: DashboardTrade[],
  accountCurrency: string,
  pivotCurrency: string,
  latestRates: Map<string, number>,
) {
  return trades
    .filter((trade) => trade.account_id === accountId && !trade.exit_date)
    .reduce((sum, trade) => {
      const qty = Number(trade.quantity || 0)
      const price = Number(trade.current_price ?? trade.entry_price ?? 0)
      if (!qty || !price) return sum
      const signed = trade.long_short === 'short' ? -(qty * price) : qty * price
      return sum + convertBetweenCurrencies(
        signed,
        trade.currency || accountCurrency,
        accountCurrency,
        pivotCurrency,
        latestRates,
      )
    }, 0)
}

function getAccountTotalValue(
  account: DashboardAccount,
  trades: DashboardTrade[],
  cashByAccount: Record<string, number>,
  pivotCurrency: string,
  latestRates: Map<string, number>,
) {
  const accountCurrency = account.currency || pivotCurrency
  const cash = getAccountCashBalance(account, cashByAccount)
  const openValue = getAccountOpenMarketValue(account.id, trades, accountCurrency, pivotCurrency, latestRates)
  return cash + openValue
}

function buildAccountLedgerRows(rows: AccountTransaction[]): AccountLedgerRow[] {
  const ordered = [...rows].sort((a, b) => {
    const byOccurred = a.occurred_at.localeCompare(b.occurred_at)
    if (byOccurred !== 0) return byOccurred
    const byCreated = (a.created_at || '').localeCompare(b.created_at || '')
    if (byCreated !== 0) return byCreated
    return a.id.localeCompare(b.id)
  })
  let running = 0
  return ordered.map((row) => {
    running += Number(row.amount) || 0
    return { ...row, runningBalance: running }
  })
}

function getRangeStart(range: RangeKey) {
  if (range === 'ALL') return null
  const date = new Date()
  if (range === 'YTD') return new Date(date.getFullYear(), 0, 1)
  if (range === '7D') date.setDate(date.getDate() - 7)
  if (range === '4W') date.setDate(date.getDate() - 28)
  if (range === '1Y') date.setFullYear(date.getFullYear() - 1)
  return date
}

function buildAccountDevelopmentChartData(
  accountTransactions: AccountTransaction[],
  range: RangeKey,
  currentTotalValue: number,
): { date: string; value: number }[] {
  const rangeStart = getRangeStart(range)
  const ordered = [...accountTransactions].sort((a, b) => a.occurred_at.localeCompare(b.occurred_at))
  let running = 0

  for (const row of ordered) {
    const at = new Date(row.occurred_at)
    if (rangeStart && at < rangeStart) running += Number(row.amount) || 0
  }

  const points: { t: number; date: string; value: number }[] = []

  if (rangeStart) {
    points.push({
      t: rangeStart.getTime(),
      date: formatDate(rangeStart.toISOString()),
      value: Number(running.toFixed(2)),
    })
  }

  for (const row of ordered) {
    const at = new Date(row.occurred_at)
    if (rangeStart && at < rangeStart) continue
    running += Number(row.amount) || 0
    points.push({
      t: at.getTime(),
      date: formatDate(row.occurred_at),
      value: Number(running.toFixed(2)),
    })
  }

  const nowIso = new Date().toISOString()
  points.push({
    t: Date.now(),
    date: formatDate(nowIso),
    value: Number(currentTotalValue.toFixed(2)),
  })

  points.sort((a, b) => a.t - b.t)

  const merged: { date: string; value: number }[] = []
  for (const point of points) {
    const last = merged[merged.length - 1]
    if (last && last.date === point.date) last.value = point.value
    else merged.push({ date: point.date, value: point.value })
  }

  return merged
}

function StudioCumulativeAreaChart({
  data,
  currency,
  gradientId,
  tooltipLabel,
  emptyDetail = 'Not enough data points in this range.',
}: {
  data: { date: string; value: number }[]
  currency: string
  gradientId: string
  tooltipLabel: string
  emptyDetail?: string
}) {
  if (data.length < 2) {
    return <EmptyState title="Not enough chart data" detail={emptyDetail} />
  }
  return (
    <ResponsiveContainer width="100%" height="100%">
      <AreaChart data={data} margin={{ top: 12, right: 8, bottom: 0, left: -18 }}>
        <defs>
          <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#77c9bd" stopOpacity={0.38} />
            <stop offset="100%" stopColor="#77c9bd" stopOpacity={0.02} />
          </linearGradient>
        </defs>
        <CartesianGrid stroke="#ececea" strokeDasharray="2 3" vertical={false} />
        <XAxis dataKey="date" tick={{ fontSize: 10, fill: '#626964' }} axisLine={false} tickLine={false} minTickGap={28} />
        <YAxis tick={{ fontSize: 10, fill: '#626964' }} axisLine={false} tickLine={false} tickFormatter={(value) => formatMoney(value, currency, true)} />
        <Tooltip contentStyle={{ borderRadius: 9, border: 0, background: '#202422', color: '#fff', fontSize: 12 }} formatter={(value) => [formatMoney(Number(value), currency), tooltipLabel]} />
        <Area type="monotone" dataKey="value" stroke="#509f96" strokeWidth={1.5} fill={`url(#${gradientId})`} activeDot={{ r: 3, fill: '#286e66' }} />
      </AreaChart>
    </ResponsiveContainer>
  )
}

function AppTest() {
  const navigate = useNavigate()
  const location = useLocation()
  const { detailId: tradeDetailId } = useParams()
  const { user, userProfile, loading: authLoading, signOut } = useAuth()
  const { membershipType } = useMembership()
  const {
    selectedCurrency,
    exchangeRates,
    latestRates,
    fetchExchangeRates,
    fetchLatestExchangeRates,
  } = useDashboardCurrency(userProfile?.preferred_currency)
  const [trades, setTrades] = useState<DashboardTrade[]>([])
  const [accounts, setAccounts] = useState<DashboardAccount[]>([])
  const [accountTransactions, setAccountTransactions] = useState<AccountTransaction[]>([])
  const [strategies, setStrategies] = useState<DashboardStrategy[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [range, setRange] = useState<RangeKey>('4W')
  const [search, setSearch] = useState('')
  const [searchOpen, setSearchOpen] = useState(false)
  const [searchIndex, setSearchIndex] = useState(0)
  const [mobileNav, setMobileNav] = useState(false)
  const [notificationsOpen, setNotificationsOpen] = useState(false)
  const [profileOpen, setProfileOpen] = useState(false)
  const [primaryMetrics, setPrimaryMetrics] = useState<MetricId[]>(() =>
    loadMetricSelection('app-test-primary-metrics', DEFAULT_PRIMARY_METRICS, 4)
  )
  const [miniMetrics, setMiniMetrics] = useState<MetricId[]>(() =>
    loadMetricSelection('app-test-mini-metrics', DEFAULT_MINI_METRICS, 6)
  )
  const [widgetFilters, setWidgetFilters] = useState<WidgetFilterMap>(loadWidgetFilters)
  const [openPositionColumns, setOpenPositionColumns] = useState<OpenPositionColumnId[]>(loadOpenPositionColumns)
  const [showOpenColumnsEditor, setShowOpenColumnsEditor] = useState(false)
  const [quickAddOpen, setQuickAddOpen] = useState(false)
  const [importOpen, setImportOpen] = useState(false)
  const [quickReviewTrade, setQuickReviewTrade] = useState<Trade | null>(null)
  const [editTrade, setEditTrade] = useState<Trade | null>(null)
  const [tradeRefresh, setTradeRefresh] = useState(0)
  const [inboxCount, setInboxCount] = useState(0)
  const [openColumnDraft, setOpenColumnDraft] = useState<{ id: OpenPositionColumnId; visible: boolean }[]>([])
  const [openPositionSort, setOpenPositionSort] = useState<{ id: OpenPositionSortKey; dir: 'asc' | 'desc' } | null>(null)
  const popoverRef = useRef<HTMLDivElement>(null)
  const searchRef = useRef<HTMLDivElement>(null)
  const searchInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (!user) return
    let cancelled = false
    if (tradeRefresh === 0) setLoading(true)
    supabase
      .from('trades_with_market_data')
      .select('*')
      .eq('user_id', user.id)
      .order('entry_date', { ascending: true })
      .then(async ({ data, error: queryError }) => {
        if (cancelled) return
        if (queryError) {
          setLoading(false)
          setError(queryError.message)
          return
        }
        const baseTrades = (data || []) as DashboardTrade[]
        try {
          const symbols = Array.from(new Set(baseTrades.map((trade) => trade.symbol).filter(Boolean)))
          const markets = Array.from(new Set(baseTrades.map((trade) => trade.market).filter(Boolean))) as string[]
          if (!symbols.length) {
            setTrades(baseTrades)
          } else {
            let symbolsQuery = supabase.from('symbols').select('symbol,market,exchange').in('symbol', symbols)
            if (markets.length) symbolsQuery = symbolsQuery.in('market', markets)
            const { data: symbolsData, error: symbolsError } = await symbolsQuery
            if (cancelled) return
            if (symbolsError || !symbolsData) {
              setTrades(baseTrades)
            } else {
              const exchangeByKey = new Map<string, string | null>()
              for (const symbol of symbolsData as Array<{ symbol: string; market: string; exchange: string | null }>) {
                const key = `${symbol.market}::${symbol.symbol}`.toUpperCase()
                if (!exchangeByKey.has(key) || (!exchangeByKey.get(key) && symbol.exchange)) {
                  exchangeByKey.set(key, symbol.exchange)
                }
              }
              setTrades(baseTrades.map((trade) => ({
                ...trade,
                exchange: exchangeByKey.get(`${trade.market}::${trade.symbol}`.toUpperCase()) || null,
              })))
            }
          }
        } catch {
          if (!cancelled) setTrades(baseTrades)
        } finally {
          if (!cancelled) setLoading(false)
        }
      })
    return () => { cancelled = true }
  }, [user?.id, tradeRefresh])

  const refreshMarketData = useCallback(() => {
    setTradeRefresh((value) => value + 1)
  }, [])
  useMarketDataRefreshInterval(refreshMarketData, Boolean(user))

  useEffect(() => {
    if (!user) return
    Promise.all([
      supabase.from('accounts').select('id,name,description,platform,currency,initial_balance').eq('user_id', user.id).order('name'),
      supabase.from('strategies').select('id,display_name,description,is_active,is_public,created_at').or(`user_id.eq.${user.id},is_public.eq.true`).order('display_name'),
      supabase
        .from('account_transactions')
        .select('id,account_id,type,amount,original_amount,original_currency,fx_rate,trade_id,occurred_at,note,source,created_at')
        .eq('user_id', user.id)
        .order('occurred_at', { ascending: true }),
    ]).then(async ([accountsResult, strategiesResult, txResult]) => {
      if (!accountsResult.error) {
        setAccounts((accountsResult.data || []).map((account) => ({ ...account, is_active: true })) as DashboardAccount[])
      } else {
        const fallback = await supabase.from('accounts').select('id,name,platform,currency').eq('user_id', user.id).order('name')
        if (!fallback.error) setAccounts((fallback.data || []).map((account) => ({ ...account, initial_balance: null, is_active: true })) as DashboardAccount[])
      }
      if (!strategiesResult.error) setStrategies((strategiesResult.data || []) as DashboardStrategy[])
      if (!txResult.error) setAccountTransactions((txResult.data || []) as AccountTransaction[])
      else setAccountTransactions([])
    })
  }, [user?.id, tradeRefresh])

  useEffect(() => {
    if (!trades.length) return
    const closedTrades = trades.filter((trade) => trade.exit_date && trade.pnl != null && trade.currency)
    fetchExchangeRates(selectedCurrency, closedTrades)
  }, [trades, selectedCurrency, fetchExchangeRates])

  useEffect(() => {
    const currencies = Array.from(new Set([
      ...trades.map((trade) => trade.currency || selectedCurrency),
      ...accounts.map((account) => account.currency || selectedCurrency),
    ]))
    if (!currencies.length) return
    fetchLatestExchangeRates(selectedCurrency, currencies)
  }, [trades, accounts, selectedCurrency, fetchLatestExchangeRates])

  useEffect(() => {
    const close = (event: MouseEvent) => {
      if (!popoverRef.current?.contains(event.target as Node)) {
        setNotificationsOpen(false)
        setProfileOpen(false)
      }
      if (!searchRef.current?.contains(event.target as Node)) setSearchOpen(false)
    }
    document.addEventListener('mousedown', close)
    return () => document.removeEventListener('mousedown', close)
  }, [])

  const filteredByRange = useMemo(() => {
    const start = getRangeStart(range)
    if (!start) return trades
    return trades.filter((trade) => {
      const date = trade.exit_date || trade.entry_date
      return date ? new Date(date) >= start : false
    })
  }, [trades, range])

  const metrics = useMemo(() => {
    const closed = filteredByRange.filter((trade) => trade.exit_date && trade.pnl != null)
    const open = filteredByRange.filter((trade) => !trade.exit_date)
    const wins = closed.filter((trade) => Number(trade.pnl) > 0)
    const losses = closed.filter((trade) => Number(trade.pnl) < 0)
    const breakeven = closed.length - wins.length - losses.length
    const totalPnl = closed.reduce((sum, trade) => sum + convertPnl(trade, selectedCurrency, exchangeRates), 0)
    const openPnl = open.reduce(
      (sum, trade) => sum + convertToDisplayCurrency(Number(trade.unrealized_pnl || 0), trade.currency || selectedCurrency, selectedCurrency, latestRates),
      0
    )
    const winRate = closed.length ? (wins.length / closed.length) * 100 : 0
    const avgRR = closed.length
      ? closed.reduce((sum, trade) => sum + Number(trade.rr || 0), 0) / closed.length
      : 0
    const grossProfit = wins.reduce((sum, trade) => sum + convertPnl(trade, selectedCurrency, exchangeRates), 0)
    const grossLoss = Math.abs(losses.reduce((sum, trade) => sum + convertPnl(trade, selectedCurrency, exchangeRates), 0))
    return {
      closed, open, wins, losses, breakeven, totalPnl, openPnl, winRate, avgRR,
      profitFactor: grossLoss ? grossProfit / grossLoss : grossProfit > 0 ? Infinity : 0,
    }
  }, [filteredByRange, selectedCurrency, exchangeRates, latestRates])

  const chartData = useMemo(() => {
    let cumulative = 0
    return metrics.closed
      .slice()
      .sort((a, b) => new Date(a.exit_date || 0).getTime() - new Date(b.exit_date || 0).getTime())
      .map((trade) => {
        cumulative += convertPnl(trade, selectedCurrency, exchangeRates)
        return { date: formatDate(trade.exit_date), value: Number(cumulative.toFixed(2)), symbol: trade.symbol }
      })
  }, [metrics.closed, selectedCurrency, exchangeRates])

  const visibleTrades = useMemo(() => {
    const term = search.trim().toLowerCase()
    return filteredByRange
      .filter((trade) => !term || `${trade.symbol} ${trade.market} ${trade.long_short}`.toLowerCase().includes(term))
      .slice()
      .sort((a, b) => new Date(b.exit_date || b.entry_date || 0).getTime() - new Date(a.exit_date || a.entry_date || 0).getTime())
  }, [filteredByRange, search])

  const visibleOpenTrades = useMemo(() => {
    const term = search.trim().toLowerCase()
    return trades
      .filter((trade) => !trade.exit_date)
      .filter((trade) => !term || `${trade.symbol} ${trade.market} ${trade.long_short}`.toLowerCase().includes(term))
      .slice()
      .sort((a, b) => new Date(b.entry_date || 0).getTime() - new Date(a.entry_date || 0).getTime())
  }, [trades, search])

  const currency = selectedCurrency
  const displayName = userProfile?.display_name || user?.user_metadata?.full_name || user?.email?.split('@')[0] || 'Trader'
  const initials = displayName.slice(0, 2).toUpperCase()
  const isTradeDetailView = Boolean(tradeDetailId)
  const currentSection = isTradeDetailView ? 'trade' : (location.pathname.split('/')[2] || 'overview')
  const tradeDetailTitle = useMemo(() => {
    if (!isTradeDetailView) return null
    const state = location.state as { trade?: { symbol?: string }; tradeId?: string } | undefined
    if (state?.trade?.symbol) return state.trade.symbol
    const tradeId = state?.tradeId
    if (tradeId) {
      const match = trades.find((trade) => trade.id === tradeId)
      if (match) return match.symbol
    }
    return 'Trade detail'
  }, [isTradeDetailView, location.state, trades])
  const pageMeta: Record<string, { title: string; subtitle: string }> = {
    overview: { title: 'Overview', subtitle: 'Here’s how your trading is performing.' },
    inbox: { title: 'Inbox', subtitle: 'Alerts, reviews and journal notifications in one place.' },
    trades: { title: 'Trades', subtitle: 'Review, search and manage your trading journal.' },
    analytics: { title: 'Analytics', subtitle: 'Understand the patterns behind your performance.' },
    accounts: { title: 'Accounts', subtitle: 'Track balances and activity across your trading accounts.' },
    strategies: { title: 'Strategies', subtitle: 'Compare the playbooks driving your results.' },
    insights: { title: 'Insights', subtitle: 'See the patterns, risks and recommendations in your journal.' },
    tools: { title: 'Tools', subtitle: 'Calculate risk, position size and performance.' },
    community: { title: 'Community', subtitle: 'Follow shared trades from the traders you watch.' },
    trade: { title: 'Trade detail', subtitle: 'Timeline, journal history and trade actions.' },
  }
  const activePage = isTradeDetailView
    ? { title: tradeDetailTitle || pageMeta.trade.title, subtitle: pageMeta.trade.subtitle }
    : pageMeta[currentSection] || pageMeta.overview

  const openTradeQuickReview = (trade: DashboardTrade, event?: React.MouseEvent) => {
    event?.stopPropagation()
    setQuickReviewTrade(trade as Trade)
  }

  const openTradeDetailPage = (trade: Trade, event?: { preventDefault: () => void; stopPropagation: () => void }) => {
    event?.preventDefault()
    event?.stopPropagation()
    setQuickReviewTrade(null)
    navigateToTradeDetail(trade, navigate, membershipType, undefined, 'app-test')
  }

  const openTradeEditor = (trade: Trade) => {
    setQuickReviewTrade(null)
    setEditTrade(trade)
    setQuickAddOpen(true)
  }

  useEffect(() => {
    const focusSearch = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault()
        searchInputRef.current?.focus()
        setSearchOpen(true)
      }
    }
    document.addEventListener('keydown', focusSearch)
    return () => document.removeEventListener('keydown', focusSearch)
  }, [])

  useEffect(() => {
    localStorage.setItem('app-test-primary-metrics', JSON.stringify(primaryMetrics))
  }, [primaryMetrics])

  useEffect(() => {
    localStorage.setItem('app-test-mini-metrics', JSON.stringify(miniMetrics))
  }, [miniMetrics])

  useEffect(() => {
    localStorage.setItem('app-test-widget-filters', JSON.stringify(widgetFilters))
  }, [widgetFilters])

  const updateMetric = (group: 'primary' | 'mini', index: number, metricId: MetricId) => {
    const setter = group === 'primary' ? setPrimaryMetrics : setMiniMetrics
    setter((current) => current.map((id, itemIndex) => itemIndex === index ? metricId : id))
  }

  const updateWidgetFilter = (slotId: string, filter: TestWidgetFilter) => {
    setWidgetFilters((current) => ({ ...current, [slotId]: filter }))
  }

  const marketOptions = Array.from(new Set(trades.map((trade) => trade.market).filter((market): market is string => Boolean(market)))).sort()
  const exchangeOptions = Array.from(new Set(trades.map((trade) => trade.exchange).filter((exchange): exchange is string => Boolean(exchange)))).sort()
  const tagOptions = Array.from(new Set(trades.flatMap((trade) => trade.tags || []))).sort()

  const getMetricPresentation = (id: MetricId, filter: TestWidgetFilter = EMPTY_WIDGET_FILTER) => {
    const scopedTrades = applyTestWidgetFilter(filteredByRange, filter)
    const scopedCalculated = calcClosedTradesMetrics(scopedTrades.map((trade) => ({
      ...trade,
      exit_date: trade.exit_date ?? null,
      entry_date: trade.entry_date ?? null,
      pnl: trade.pnl ?? null,
      pnl_percentage: trade.pnl_percentage ?? null,
      strategy_id: trade.strategy_id ?? null,
      convertedPnl: convertPnl(trade, currency, exchangeRates),
    })))
    const scopedOpen = scopedTrades.filter((trade) => !trade.exit_date)
    const scopedWins = scopedTrades.filter((trade) => trade.exit_date && Number(trade.pnl || 0) > 0)
    const scopedLosses = scopedTrades.filter((trade) => trade.exit_date && Number(trade.pnl || 0) < 0)
    const scopedOpenPnl = scopedOpen.reduce(
      (sum, trade) => sum + convertToDisplayCurrency(Number(trade.unrealized_pnl || 0), trade.currency || currency, currency, latestRates),
      0
    )
    const scopedInvested = scopedOpen.reduce(
      (sum, trade) => sum + convertToDisplayCurrency(Number(trade.total_entry_amount || trade.position_size || 0), trade.currency || currency, currency, latestRates),
      0
    )
    const scopedPercentages = scopedOpen.filter((trade) => trade.unrealized_pnl_percentage != null)
    const scopedOpenPnlPercent = scopedInvested > 0
      ? scopedOpenPnl / scopedInvested * 100
      : scopedPercentages.length
        ? scopedPercentages.reduce((sum, trade) => sum + Number(trade.unrealized_pnl_percentage || 0), 0) / scopedPercentages.length
        : 0
    const scopedTotalRisk = scopedOpen.reduce((sum, trade) => {
      const entry = Number(trade.entry_price || 0)
      const stop = Number(trade.stop_loss || 0)
      if (!entry || !stop) return sum
      const priceRisk = Math.abs(entry - stop)
      const quantity = Number(trade.quantity || 0)
      const contract = Number(trade.contract_size || 1)
      if (quantity > 0) return sum + convertToDisplayCurrency(priceRisk * quantity * contract, trade.currency || currency, currency, latestRates)
      const positionSize = Number(trade.position_size || trade.total_entry_amount || 0)
      const riskInTradeCurrency = positionSize > 0 ? positionSize * priceRisk / entry : 0
      return sum + convertToDisplayCurrency(riskInTradeCurrency, trade.currency || currency, currency, latestRates)
    }, 0)
    const scopedAccounts = filter.accountIds.length ? accounts.filter((account) => filter.accountIds.includes(account.id)) : accounts
    const accountsWithBalance = scopedAccounts.filter((account) => account.initial_balance != null && !Number.isNaN(Number(account.initial_balance)))
    const scopedAccountIds = new Set(accountsWithBalance.map((account) => account.id))
    const scopedAccountBalance = accountsWithBalance.reduce(
      (sum, account) => sum + convertToDisplayCurrency(Number(account.initial_balance), account.currency || currency, currency, latestRates),
      0
    )
    const accountTrades = trades.filter((trade) => trade.account_id && scopedAccountIds.has(trade.account_id))
    const realizedAccountPnl = accountTrades
      .filter((trade) => trade.exit_date && trade.pnl != null)
      .reduce((sum, trade) => sum + convertPnl(trade, currency, exchangeRates), 0)
    const unrealizedAccountPnl = accountTrades
      .filter((trade) => !trade.exit_date && trade.unrealized_pnl != null)
      .reduce(
        (sum, trade) => sum + convertToDisplayCurrency(Number(trade.unrealized_pnl || 0), trade.currency || currency, currency, latestRates),
        0
      )
    const currentAccountEquity = scopedAccountBalance + realizedAccountPnl + unrealizedAccountPnl
    const scopedRiskPercent = currentAccountEquity > 0 ? scopedTotalRisk / currentAccountEquity * 100 : null
    const option = METRIC_OPTIONS.find((metric) => metric.id === id)
    const percentValue = (value: number | null) => value == null ? '—' : `${value >= 0 ? '+' : ''}${value.toFixed(2)}%`
    switch (id) {
      case 'openPositions':
        return { label: option!.label, context: 'Live positions', value: String(scopedOpen.length), progress: Math.min(scopedOpen.length * 15, 100), tone: 'blue', footer: `${formatMoney(scopedOpenPnl, currency)} unrealized` }
      case 'totalPnLPercent':
        return { label: option!.label, context: 'Open return', value: percentValue(scopedOpenPnlPercent), progress: Math.min(Math.abs(scopedOpenPnlPercent) * 5, 100), tone: scopedOpenPnlPercent >= 0 ? 'mint' : 'rose', footer: `${scopedOpen.length} open positions` }
      case 'totalPnlOpen':
        return { label: option!.label, context: 'Unrealized', value: formatMoney(scopedOpenPnl, currency), progress: Math.min(Math.abs(scopedOpenPnl) / 20, 100), tone: scopedOpenPnl >= 0 ? 'mint' : 'rose', footer: `${scopedOpen.length} marked positions` }
      case 'totalRiskExposure':
        return {
          label: option!.label,
          context: scopedRiskPercent == null ? 'Stop-loss risk' : `${scopedRiskPercent.toFixed(2)}% risk`,
          value: scopedRiskPercent == null
            ? formatMoney(scopedTotalRisk, currency)
            : `${formatMoney(scopedTotalRisk, currency)} · ${scopedRiskPercent.toFixed(2)}%`,
          progress: Math.min(scopedRiskPercent ?? scopedTotalRisk / 20, 100),
          tone: 'amber',
          footer: scopedRiskPercent == null ? 'Initial account balance is required' : 'Risk exposure / current account equity',
        }
      case 'totalPnl':
        return { label: option!.label, context: `${range} realized`, value: formatMoney(scopedCalculated.totalPnl, currency), progress: Math.min(Math.abs(scopedCalculated.totalPnl) / 20, 100), tone: scopedCalculated.totalPnl >= 0 ? 'mint' : 'rose', footer: `${scopedCalculated.totalTrades} closed trades` }
      case 'totalTrades':
        return { label: option!.label, context: 'Closed trades', value: String(scopedCalculated.totalTrades), progress: Math.min(scopedCalculated.totalTrades * 4, 100), tone: 'blue', footer: `${scopedTrades.length} total journal entries` }
      case 'winRate':
        return { label: option!.label, context: 'Closed trades', value: `${scopedCalculated.winRate.toFixed(1)}%`, progress: scopedCalculated.winRate, tone: 'blue', footer: `${scopedWins.length} wins · ${scopedLosses.length} losses` }
      case 'profitFactor':
        return { label: option!.label, context: 'Gross profit / loss', value: scopedCalculated.profitFactor === Infinity ? '∞' : scopedCalculated.profitFactor.toFixed(2), progress: Math.min(scopedCalculated.profitFactor * 30, 100), tone: 'amber', footer: scopedCalculated.profitFactor >= 1 ? 'Profitable distribution' : 'Below breakeven' }
      case 'avgPnlPercent':
        return { label: option!.label, context: 'Simple average', value: percentValue(scopedCalculated.avgPnlPct), progress: Math.min(Math.abs(scopedCalculated.avgPnlPct || 0) * 10, 100), tone: (scopedCalculated.avgPnlPct || 0) >= 0 ? 'mint' : 'rose', footer: 'Per closed trade' }
      case 'weightedPnlPercent':
        return { label: option!.label, context: 'Position weighted', value: percentValue(scopedCalculated.weightedPnlPct), progress: Math.min(Math.abs(scopedCalculated.weightedPnlPct || 0) * 10, 100), tone: (scopedCalculated.weightedPnlPct || 0) >= 0 ? 'mint' : 'rose', footer: 'Weighted by trade size' }
      case 'expectancy':
        return { label: option!.label, context: 'Expected outcome', value: scopedCalculated.expectancy == null ? '—' : `${scopedCalculated.expectancy >= 0 ? '+' : ''}${scopedCalculated.expectancy.toFixed(2)}R`, progress: Math.min(Math.abs(scopedCalculated.expectancy || 0) * 30, 100), tone: (scopedCalculated.expectancy || 0) >= 0 ? 'mint' : 'rose', footer: 'Expected R per trade' }
      case 'averageWin':
        return { label: option!.label, context: 'Winning trades', value: formatMoney(scopedCalculated.avgWin, currency), progress: Math.min(scopedCalculated.avgWin / 20, 100), tone: 'mint', footer: `${scopedWins.length} winning trades` }
      case 'averageLoss':
        return { label: option!.label, context: 'Losing trades', value: formatMoney(-scopedCalculated.avgLoss, currency), progress: Math.min(scopedCalculated.avgLoss / 20, 100), tone: 'rose', footer: `${scopedLosses.length} losing trades` }
      case 'averageRR':
        return { label: option!.label, context: 'Reward / risk', value: scopedCalculated.avgRR.toFixed(2), progress: Math.min(scopedCalculated.avgRR * 25, 100), tone: 'amber', footer: 'Across closed trades' }
    }
  }

  const getTradeDisplayPnl = (trade: DashboardTrade) =>
    trade.exit_date
      ? convertPnl(trade, currency, exchangeRates)
      : convertToDisplayCurrency(Number(trade.unrealized_pnl || 0), trade.currency || currency, currency, latestRates)

  const sortedVisibleOpenTrades = useMemo(() => {
    if (!openPositionSort) return visibleOpenTrades
    const direction = openPositionSort.dir === 'asc' ? 1 : -1
    return [...visibleOpenTrades].sort((left, right) => {
      const a = openPositionSortValue(left, openPositionSort.id, accounts, getTradeDisplayPnl)
      const b = openPositionSortValue(right, openPositionSort.id, accounts, getTradeDisplayPnl)
      const aEmpty = a == null || a === ''
      const bEmpty = b == null || b === ''
      if (aEmpty && bEmpty) return 0
      if (aEmpty) return 1
      if (bEmpty) return -1
      if (typeof a === 'number' && typeof b === 'number') return (a - b) * direction
      return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' }) * direction
    })
  }, [visibleOpenTrades, openPositionSort, accounts, currency, exchangeRates, latestRates])

  const toggleOpenPositionSort = (columnId: OpenPositionSortKey) => {
    setOpenPositionSort((current) => {
      if (!current || current.id !== columnId) return { id: columnId, dir: 'asc' }
      if (current.dir === 'asc') return { id: columnId, dir: 'desc' }
      return null
    })
  }

  const searchResults = useMemo<SearchResult[]>(() => {
    const term = search.trim().toLowerCase()
    if (!term) return []
    if (currentSection === 'accounts') {
      return accounts
        .filter((account) => `${account.name} ${account.platform || ''} ${account.currency || ''}`.toLowerCase().includes(term))
        .slice(0, 8)
        .map((account) => ({
          id: account.id,
          kind: 'Account' as const,
          title: account.name,
          meta: [account.platform || 'Trading account', account.currency || currency].join(' · '),
          path: `/app-test/accounts?account=${account.id}`,
        }))
    }
    if (currentSection === 'strategies') {
      return strategies
        .filter((strategy) => `${strategy.display_name} ${strategy.description || ''}`.toLowerCase().includes(term))
        .slice(0, 8)
        .map((strategy) => ({
          id: strategy.id,
          kind: 'Strategy' as const,
          title: strategy.display_name,
          meta: strategy.description || 'No description',
          path: `/app/strategy/${encodeURIComponent(strategy.display_name)}`,
        }))
    }
    return trades
      .filter((trade) => `${trade.symbol} ${trade.market || ''} ${trade.exchange || ''} ${trade.long_short || ''} ${(trade.tags || []).join(' ')}`.toLowerCase().includes(term))
      .slice()
      .sort((a, b) => new Date(b.exit_date || b.entry_date || 0).getTime() - new Date(a.exit_date || a.entry_date || 0).getTime())
      .slice(0, 8)
      .map((trade) => {
        const pnl = getTradeDisplayPnl(trade)
        return {
          id: trade.id,
          kind: trade.exit_date ? 'Closed' as const : 'Open' as const,
          title: trade.symbol,
          meta: [trade.market || 'Market', trade.long_short || 'long', formatDate(trade.exit_date || trade.entry_date)].join(' · '),
          path: `/app-test/trade/${trade.id}`,
          amount: `${pnl >= 0 ? '+' : ''}${formatMoney(pnl, currency)}`,
          tone: pnl >= 0 ? 'positive' as const : 'negative' as const,
          market: trade.market ?? undefined,
          exchange: trade.exchange ?? undefined,
        }
      })
  }, [search, currentSection, accounts, strategies, trades, currency, exchangeRates, latestRates])

  useEffect(() => {
    setSearchIndex(0)
  }, [search, currentSection])

  const openSearchResult = (result: SearchResult) => {
    setSearchOpen(false)
    if (result.kind === 'Open' || result.kind === 'Closed') {
      const trade = trades.find((entry) => entry.id === result.id)
      if (trade) {
        openTradeQuickReview(trade)
        if (currentSection !== 'trades' && currentSection !== 'overview') navigate('/app-test/trades')
        return
      }
    }
    navigate(result.path)
  }

  const handleSearchKeyDown = (event: ReactKeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault()
      if (!searchResults.length) return
      setSearchOpen(true)
      setSearchIndex((index) => Math.min(index + 1, searchResults.length - 1))
    } else if (event.key === 'ArrowUp') {
      event.preventDefault()
      setSearchIndex((index) => Math.max(index - 1, 0))
    } else if (event.key === 'Enter' && searchOpen && searchResults[searchIndex]) {
      event.preventDefault()
      openSearchResult(searchResults[searchIndex])
    } else if (event.key === 'Escape') {
      setSearchOpen(false)
    }
  }

  const openColumnsEditor = () => {
    const visible = new Set(openPositionColumns)
    setOpenColumnDraft([
      ...openPositionColumns.map((id) => ({ id, visible: true })),
      ...OPEN_POSITION_COLUMNS.filter((column) => !visible.has(column.id)).map((column) => ({ id: column.id, visible: false })),
    ])
    setShowOpenColumnsEditor(true)
  }

  const moveOpenColumn = (index: number, direction: -1 | 1) => {
    setOpenColumnDraft((current) => {
      const target = index + direction
      if (target < 0 || target >= current.length) return current
      const next = [...current]
      ;[next[index], next[target]] = [next[target], next[index]]
      return next
    })
  }

  const saveOpenColumns = () => {
    const selected = openColumnDraft.filter((column) => column.visible).map((column) => column.id)
    if (!selected.length) return
    setOpenPositionColumns(selected)
    localStorage.setItem('app-test-open-position-columns', JSON.stringify(selected))
    setShowOpenColumnsEditor(false)
  }

  const renderOpenPositionCell = (trade: DashboardTrade, columnId: OpenPositionColumnId) => {
    const tradeCurrency = trade.currency || currency
    const positionSize = trade.position_size ?? (trade.quantity != null ? Number(trade.quantity) * Number(trade.contract_size || 1) : null)
    const totalInvested = trade.total_entry_amount ?? (trade.quantity != null && trade.entry_price != null ? Number(trade.quantity) * Number(trade.entry_price) : null)
    const averageCostBasis = trade.cost_basis_remaining ?? (trade.long_short === 'long' ? trade.total_entry_amount : trade.total_exit_amount)
    const averageCost = trade.quantity && Number(trade.quantity) > 0 && averageCostBasis != null && Number(averageCostBasis) > 0
      ? Number(averageCostBasis) / Number(trade.quantity)
      : trade.entry_price
    const risk = trade.entry_price != null && trade.stop_loss != null
      ? Math.abs(Number(trade.entry_price) - Number(trade.stop_loss)) * Number(trade.quantity || 0) * Number(trade.contract_size || 1)
      : null
    const riskPercent = risk != null && totalInvested ? risk / Number(totalInvested) * 100 : null
    const pnl = getTradeDisplayPnl(trade)
    switch (columnId) {
      case 'side':
        return <span className={`at-side ${trade.long_short === 'short' ? 'short' : ''}`}>{trade.long_short || 'long'}</span>
      case 'opened':
        return formatDate(trade.entry_date)
      case 'entry':
        return trade.entry_price != null ? formatMoney(Number(trade.entry_price), tradeCurrency) : '—'
      case 'averageCost':
        return averageCost != null && averageCost > 0 ? formatMoney(averageCost, tradeCurrency) : '—'
      case 'positionSize':
        return positionSize != null ? formatMoney(Number(positionSize), tradeCurrency) : '—'
      case 'totalInvested':
        return totalInvested != null ? formatMoney(Number(totalInvested), tradeCurrency) : '—'
      case 'sl':
        return trade.stop_loss != null ? formatMoney(Number(trade.stop_loss), tradeCurrency) : '—'
      case 'tp':
        return trade.take_profit != null ? formatMoney(Number(trade.take_profit), tradeCurrency) : '—'
      case 'risk':
        return risk != null ? formatMoney(risk, tradeCurrency) : '—'
      case 'riskPercent':
        return riskPercent != null ? `${riskPercent.toFixed(2)}%` : '—'
      case 'price':
        return trade.current_price != null ? formatMoney(Number(trade.current_price), tradeCurrency) : '—'
      case 'pnlPercent':
        return trade.unrealized_pnl_percentage != null
          ? <span className={Number(trade.unrealized_pnl_percentage) >= 0 ? 'positive' : 'negative'}>{Number(trade.unrealized_pnl_percentage) >= 0 ? '+' : ''}{Number(trade.unrealized_pnl_percentage).toFixed(2)}%</span>
          : '—'
      case 'pnl':
        return <span className={pnl >= 0 ? 'positive' : 'negative'}>{pnl >= 0 ? '+' : ''}{formatMoney(pnl, currency)}</span>
      case 'account':
        return accounts.find((account) => account.id === trade.account_id)?.name || '—'
      case 'exchange':
        return trade.exchange || '—'
      case 'tags':
        return trade.tags?.length ? <span className="at-open-tags">{trade.tags.join(', ')}</span> : '—'
    }
  }

  const exportCsv = () => {
    const rows = visibleTrades.map((trade) => [
      trade.symbol, trade.market || '', trade.long_short || '', trade.entry_date || '',
      trade.exit_date || '', trade.pnl ?? '', trade.pnl_percentage ?? '', trade.currency || '',
    ])
    const csv = [
      ['Symbol', 'Market', 'Side', 'Entry date', 'Exit date', 'P/L', 'P/L %', 'Currency'],
      ...rows,
    ].map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(',')).join('\n')
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }))
    const anchor = document.createElement('a')
    anchor.href = url
    anchor.download = `traderhub24-${range.toLowerCase()}-report.csv`
    anchor.click()
    URL.revokeObjectURL(url)
  }

  const handleInboxCount = useCallback((count: number) => setInboxCount(count), [])
  const feedTrades = useMemo(() => trades.map((trade) => ({
    ...trade,
    long_short: trade.long_short === 'short' ? 'short' as const : 'long' as const,
    entry_price: Number(trade.entry_price || 0),
    stop_loss: trade.stop_loss ?? null,
    take_profit: trade.take_profit ?? null,
    strategy: null as string | null,
    strategy_id: trade.strategy_id ?? null,
  })), [trades])
  const closedTradesLast2Weeks = useMemo(() => {
    const start = new Date()
    start.setDate(start.getDate() - 14)
    return feedTrades.filter((trade) => trade.exit_date && new Date(trade.exit_date) >= start)
  }, [feedTrades])
  const riskFeedWidgets = useMemo(() => {
    const slots = [
      ...primaryMetrics.map((metricId, index) => ({ metricId, key: `primary-${index}` })),
      ...miniMetrics.map((metricId, index) => ({ metricId, key: `mini-${index}` })),
    ]
    return slots.flatMap(({ metricId, key }) => {
      if (metricId !== 'totalRiskExposure') return []
      const filter = widgetFilters[key] || EMPTY_WIDGET_FILTER
      const market = filter.markets.length === 1 && ['Commodity', 'Crypto', 'Forex', 'Stock'].includes(filter.markets[0])
        ? filter.markets[0] as WidgetMarket
        : 'all'
      const filterSettings: WidgetFilterSettings = {
        ...DEFAULT_WIDGET_FILTER,
        strategyId: filter.strategyIds.length === 1 ? filter.strategyIds[0] : '',
        exchange: filter.exchanges.length === 1 ? filter.exchanges[0] : 'all',
        market,
        accountId: filter.accountIds.length === 1 ? filter.accountIds[0] : '',
        tag: filter.tags.length === 1 ? filter.tags[0] : '',
      }
      return [{ id: key, filterSettings }]
    })
  }, [primaryMetrics, miniMetrics, widgetFilters])
  const inboxFeed = (
    <DashboardFeed
      presentation={currentSection === 'inbox' ? 'studio' : 'count'}
      detailBasePath="app-test"
      onStudioOpenTrade={(trade) => openTradeQuickReview(trade as unknown as Trade)}
      onStudioOpenTradeReview={(trade) => {
        navigateToTradeDetail(trade as unknown as Trade, navigate, membershipType, { openAddEventWithType: 'review' }, 'app-test')
      }}
      onStudioEditTrade={(trade) => openTradeEditor(trade as unknown as Trade)}
      onCountChange={handleInboxCount}
      openTrades={feedTrades.filter((trade) => !trade.exit_date) as never}
      closedTradesLast2Weeks={closedTradesLast2Weeks as never}
      trades={feedTrades as never}
      accounts={accounts.map((account) => ({
        id: account.id,
        currency: account.currency || currency,
        initial_balance: account.initial_balance,
      }))}
      selectedCurrency={currency}
      latestRates={latestRates}
      totalRiskExposureWidgets={riskFeedWidgets}
      refreshTrigger={tradeRefresh}
      onTradeUpdated={() => setTradeRefresh((value) => value + 1)}
    />
  )

  if (authLoading) return <div className="app-test-status">Loading workspace…</div>
  if (!user) return <Navigate to="/?login=1" replace />

  return (
    <div className="app-test">
      <div className="at-backdrop-orb at-orb-one" />
      <div className="at-backdrop-orb at-orb-two" />
      <div className="at-shell">
        {mobileNav && <button className="at-mobile-scrim" aria-label="Close navigation" onClick={() => setMobileNav(false)} />}
        <aside className={`at-sidebar ${mobileNav ? 'is-open' : ''}`}>
          <div className="at-brand">
            <img src={logo} alt="TraderHub24" />
            <button className="at-icon-button at-mobile-close" onClick={() => setMobileNav(false)} aria-label="Close navigation"><Icon name="close" /></button>
          </div>

          <nav className="at-nav">
            {navSections.map((section) => (
              <div className="at-nav-section" key={section.label}>
                <p>{section.label}</p>
                {section.items.map((item) => (
                  <button
                    key={item.path}
                    className={location.pathname === item.path || (item.path === '/app-test' && currentSection === 'overview') ? 'active' : ''}
                    onClick={() => { navigate(item.path); setMobileNav(false) }}
                  >
                    <Icon name={item.icon} size={16} />
                    <span>{item.label}</span>
                    {item.label === 'Insights' && <i>New</i>}
                    {item.label === 'Inbox' && inboxCount > 0 && <i>{inboxCount > 99 ? '99+' : inboxCount}</i>}
                  </button>
                ))}
              </div>
            ))}
            <button className="at-add-trade" onClick={() => setQuickAddOpen(true)}>
              <Icon name="add" size={17} /> Add trade
            </button>
          </nav>

          <div className="at-sidebar-user">
            <button onClick={() => setProfileOpen((value) => !value)}>
              <span className="at-avatar">{initials}</span>
              <span><strong>{displayName}</strong><small>{user.email}</small></span>
              <Icon name="chevron" size={14} />
            </button>
          </div>
        </aside>

        <main className={`at-main${isTradeDetailView ? ' at-main--trade-detail' : ''}`}>
          <header className={`at-header${isTradeDetailView ? ' at-header--trade-detail' : ''}`}>
            <button className="at-icon-button at-menu-button" onClick={() => setMobileNav(true)} aria-label="Open navigation"><Icon name="menu" /></button>
            <div className="at-heading">
              {isTradeDetailView && (
                <button type="button" className="at-trade-detail-back" onClick={() => navigate('/app-test/trades')}>
                  <Icon name="chevron" size={14} /> Back to trades
                </button>
              )}
              {!isTradeDetailView && (
                <>
                  <h1>{activePage.title}</h1>
                  <p>{activePage.subtitle}</p>
                </>
              )}
            </div>
            <div className="at-header-actions" ref={popoverRef}>
              <div className="at-search-wrap" ref={searchRef}>
                <label className="at-search">
                  <Icon name="search" size={15} />
                  <input
                    ref={searchInputRef}
                    value={search}
                    role="combobox"
                    aria-expanded={searchOpen && !!search.trim()}
                    aria-controls="at-search-results"
                    aria-activedescendant={searchOpen && searchResults[searchIndex] ? `at-search-result-${searchResults[searchIndex].id}` : undefined}
                    onChange={(event) => { setSearch(event.target.value); setSearchOpen(true) }}
                    onFocus={() => setSearchOpen(true)}
                    onKeyDown={handleSearchKeyDown}
                    placeholder={currentSection === 'accounts' ? 'Search accounts…' : currentSection === 'strategies' ? 'Search strategies…' : 'Search trades…'}
                  />
                  <kbd>⌘ K</kbd>
                </label>
                {searchOpen && search.trim() && (
                  <div className="at-search-results" id="at-search-results" role="listbox">
                    {searchResults.length ? searchResults.map((result, index) => (
                      <button
                        key={result.id}
                        id={`at-search-result-${result.id}`}
                        role="option"
                        aria-selected={index === searchIndex}
                        className={index === searchIndex ? 'is-active' : ''}
                        onMouseEnter={() => setSearchIndex(index)}
                        onMouseDown={(event) => event.preventDefault()}
                        onClick={() => openSearchResult(result)}
                      >
                        {result.market ? (
                          <SymbolLogo symbol={result.title} market={result.market} exchange={result.exchange} size={22} />
                        ) : (
                          <span className="at-symbol-avatar">{result.title.slice(0, 2).toUpperCase()}</span>
                        )}
                        <span><strong>{result.title}</strong><small>{result.meta}</small></span>
                        <span className="at-search-result-aside">
                          {result.amount && <em className={result.tone}>{result.amount}</em>}
                          <i>{result.kind}</i>
                        </span>
                      </button>
                    )) : <p>No matches for “{search.trim()}”</p>}
                  </div>
                )}
              </div>
              <button
                className={`at-icon-button at-inbox-button ${currentSection === 'inbox' ? 'is-active' : ''}`}
                onClick={() => { setNotificationsOpen(false); setProfileOpen(false); navigate('/app-test/inbox') }}
                aria-label="Inbox"
              >
                <Icon name="inbox" size={17} />
                {inboxCount > 0 && <em>{inboxCount > 9 ? '9+' : inboxCount}</em>}
              </button>
              <button className="at-icon-button at-notification-button" onClick={() => { setNotificationsOpen((value) => !value); setProfileOpen(false) }} aria-label="Notifications">
                <Icon name="bell" size={17} /><span />
              </button>
              <div className="at-ranges" aria-label="Reporting range">
                {(['7D', '4W', 'YTD', '1Y', 'ALL'] as RangeKey[]).map((item) => (
                  <button key={item} className={range === item ? 'active' : ''} onClick={() => setRange(item)}>{item}</button>
                ))}
              </div>
              <button className="at-export" onClick={exportCsv}><Icon name="download" size={15} /> Export</button>
              {notificationsOpen && (
                <div className="at-popover at-notifications">
                  <div><strong>Notifications</strong><button onClick={() => setNotificationsOpen(false)}>Done</button></div>
                  <p><span className="positive-dot" /> Your {range} report is ready to export.</p>
                  {metrics.open.length > 0 && <p><span className="warning-dot" /> {metrics.open.length} open position{metrics.open.length === 1 ? '' : 's'} need your attention.</p>}
                  <p><span className="neutral-dot" /> Dashboard data is synced with your journal.</p>
                </div>
              )}
              {profileOpen && (
                <div className="at-popover at-profile-menu">
                  <button onClick={() => navigate('/app/account')}>Account settings</button>
                  <button onClick={() => navigate('/app')}>Classic dashboard</button>
                  <button className="danger" onClick={() => signOut()}>Sign out</button>
                </div>
              )}
            </div>
          </header>
          {currentSection !== 'inbox' && !isTradeDetailView && inboxFeed}

          {error ? (
            <div className="at-error">Dashboard data could not be loaded. <span>{error}</span></div>
          ) : loading ? (
            <div className="at-loading-grid">{Array.from({ length: 8 }).map((_, index) => <div key={index} />)}</div>
          ) : isTradeDetailView ? (
            <section className="at-workspace at-trade-detail-shell">
              <TradeDetail embedded appearance="studio" />
            </section>
          ) : currentSection === 'overview' ? (
            <>
              <section className="at-kpi-grid">
                {primaryMetrics.map((metricId, index) => (
                  <MetricCard
                    key={`primary-${index}`}
                    {...getMetricPresentation(metricId, widgetFilters[`primary-${index}`] || EMPTY_WIDGET_FILTER)}
                    selectedMetric={metricId}
                    onMetricChange={(nextMetric) => updateMetric('primary', index, nextMetric)}
                    filter={widgetFilters[`primary-${index}`] || EMPTY_WIDGET_FILTER}
                    onFilterChange={(filter) => updateWidgetFilter(`primary-${index}`, filter)}
                    filterOptions={{ strategies, accounts, markets: marketOptions, exchanges: exchangeOptions, tags: tagOptions }}
                  />
                ))}
              </section>

              <section className="at-mini-metrics">
                {miniMetrics.map((metricId, index) => (
                  <MiniMetricCard
                    key={`mini-${index}`}
                    metric={getMetricPresentation(metricId, widgetFilters[`mini-${index}`] || EMPTY_WIDGET_FILTER)}
                    selectedMetric={metricId}
                    onMetricChange={(nextMetric) => updateMetric('mini', index, nextMetric)}
                    filter={widgetFilters[`mini-${index}`] || EMPTY_WIDGET_FILTER}
                    onFilterChange={(filter) => updateWidgetFilter(`mini-${index}`, filter)}
                    filterOptions={{ strategies, accounts, markets: marketOptions, exchanges: exchangeOptions, tags: tagOptions }}
                  />
                ))}
              </section>

              <section className="at-chart-row">
                <article className="at-card at-open-positions-card">
                  <div className="at-section-heading">
                    <div><strong>Open positions</strong><span>{visibleOpenTrades.length} active trades</span></div>
                    <span className="at-table-heading-actions">
                      <button onClick={openColumnsEditor} title="Edit table columns"><Icon name="columns" size={13} /> Edit columns</button>
                      <button onClick={() => navigate('/app-test/trades')}>View trades <Icon name="chevron" size={12} /></button>
                    </span>
                  </div>
                  {visibleOpenTrades.length ? (
                    <div className="at-open-positions-list">
                      <table>
                        <thead>
                          <tr>
                            <th>
                              <button type="button" className={`at-th-sort ${openPositionSort?.id === 'instrument' ? 'active' : ''}`} onClick={() => toggleOpenPositionSort('instrument')}>
                                Instrument
                                <span aria-hidden="true">{openPositionSort?.id === 'instrument' ? (openPositionSort.dir === 'asc' ? '↑' : '↓') : '↕'}</span>
                              </button>
                            </th>
                            {openPositionColumns.map((columnId) => {
                              const label = OPEN_POSITION_COLUMNS.find((column) => column.id === columnId)?.label || columnId
                              const active = openPositionSort?.id === columnId
                              return (
                                <th key={columnId}>
                                  <button type="button" className={`at-th-sort ${active ? 'active' : ''}`} onClick={() => toggleOpenPositionSort(columnId)}>
                                    {label}
                                    <span aria-hidden="true">{active ? (openPositionSort?.dir === 'asc' ? '↑' : '↓') : '↕'}</span>
                                  </button>
                                </th>
                              )
                            })}
                            <th className="at-open-actions-head" aria-label="Actions" />
                          </tr>
                        </thead>
                        <tbody>
                          {sortedVisibleOpenTrades.map((trade) => {
                            return (
                              <tr key={trade.id} onClick={(event) => openTradeQuickReview(trade, event)}>
                                <td><SymbolLogo symbol={trade.symbol} market={trade.market} exchange={trade.exchange} size={20} /><span><strong>{trade.symbol}</strong><small>{trade.market || 'Market'}</small></span></td>
                                {openPositionColumns.map((columnId) => <td key={columnId}>{renderOpenPositionCell(trade, columnId)}</td>)}
                                <td className="at-open-actions-cell" onClick={(event) => event.stopPropagation()}>
                                  <TradeListQuickActions
                                    trade={trade as Trade}
                                    onUpdated={() => setTradeRefresh((value) => value + 1)}
                                    onEditTrade={openTradeEditor}
                                  />
                                </td>
                              </tr>
                            )
                          })}
                        </tbody>
                      </table>
                    </div>
                  ) : <EmptyState title="No open positions" detail="Your active trades will appear here." />}
                </article>

                <article className="at-card at-performance-card">
                  <div className="at-card-title">
                    <div><span>Cumulative P/L</span><strong>{formatMoney(metrics.totalPnl, currency)}</strong><small className={metrics.totalPnl >= 0 ? 'positive' : 'negative'}>{metrics.totalPnl >= 0 ? '↗' : '↘'} {range} performance</small></div>
                    <button onClick={() => navigate('/app-test/analytics')}>View analytics <Icon name="chevron" size={13} /></button>
                  </div>
                  <div className="at-chart">
                    <StudioCumulativeAreaChart
                      data={chartData}
                      currency={currency}
                      gradientId="atArea"
                      tooltipLabel="P/L"
                      emptyDetail="Close at least two trades in this range."
                    />
                  </div>
                </article>
              </section>

              <section className="at-bottom-row at-bottom-single">
                <article className="at-card at-calendar-card" aria-label="Calendar">
                  <div className="at-calendar">
                    {Array.from({ length: 9 }, (_, index) => {
                      const date = new Date()
                      date.setHours(12, 0, 0, 0)
                      date.setDate(date.getDate() - (8 - index))
                      const isToday = index === 8
                      return (
                        <div key={date.toISOString().slice(0, 10)} className={isToday ? 'is-today' : ''}>
                          <small>{date.toLocaleDateString('en-US', { weekday: 'short' })}</small>
                          <strong>{date.getDate()}</strong>
                          <span>{date.toLocaleDateString('en-US', { month: 'short' })}</span>
                        </div>
                      )
                    })}
                  </div>
                </article>
              </section>

              {showOpenColumnsEditor && (
                <div className="at-columns-modal-backdrop" role="dialog" aria-modal="true" aria-label="Edit open position table columns" onClick={() => setShowOpenColumnsEditor(false)}>
                  <div className="at-columns-modal" onClick={(event) => event.stopPropagation()}>
                    <div className="at-columns-modal-title">
                      <span><strong>Edit table columns</strong><small>Select columns and change their order.</small></span>
                      <button onClick={() => setShowOpenColumnsEditor(false)} aria-label="Close"><Icon name="close" size={17} /></button>
                    </div>
                    <div className="at-columns-modal-list">
                      {openColumnDraft.map((column, index) => (
                        <div key={column.id}>
                          <label>
                            <input
                              type="checkbox"
                              checked={column.visible}
                              onChange={() => setOpenColumnDraft((current) => current.map((item) => item.id === column.id ? { ...item, visible: !item.visible } : item))}
                            />
                            <span>{OPEN_POSITION_COLUMNS.find((item) => item.id === column.id)?.label}</span>
                          </label>
                          <span>
                            <button disabled={index === 0} onClick={() => moveOpenColumn(index, -1)} aria-label="Move column up">↑</button>
                            <button disabled={index === openColumnDraft.length - 1} onClick={() => moveOpenColumn(index, 1)} aria-label="Move column down">↓</button>
                          </span>
                        </div>
                      ))}
                    </div>
                    <div className="at-columns-modal-actions">
                      <button onClick={() => setShowOpenColumnsEditor(false)}>Cancel</button>
                      <button className="primary" onClick={saveOpenColumns} disabled={!openColumnDraft.some((column) => column.visible)}>Save columns</button>
                    </div>
                  </div>
                </div>
              )}
            </>
          ) : currentSection === 'insights' || currentSection === 'tools' || currentSection === 'community' ? (
            <section className="at-workspace">
              {currentSection === 'insights' && (
                <Insights onBack={() => navigate('/app-test')} setCurrentPage={() => navigate('/app-test')} embedded />
              )}
              {currentSection === 'tools' && (
                <Tools onBack={() => navigate('/app-test')} setCurrentPage={() => navigate('/app-test')} embedded />
              )}
              {currentSection === 'community' && (
                <Community onBack={() => navigate('/app-test')} setCurrentPage={() => navigate('/app-test')} embedded />
              )}
            </section>
          ) : currentSection === 'inbox' ? (
            <section className="at-inbox at-inbox-studio-page">
              <article className="at-card at-inbox-studio-panel">
                {inboxFeed}
              </article>
            </section>
          ) : (
            <TestSubpage
              section={currentSection}
              trades={visibleTrades}
              allTrades={filteredByRange}
              portfolioTrades={trades}
              range={range}
              accounts={accounts}
              transactions={accountTransactions}
              selectedAccountId={new URLSearchParams(location.search).get('account')}
              strategies={strategies}
              metrics={metrics}
              chartData={chartData}
              currency={currency}
              search={search}
              navigate={navigate}
              onAddTrade={() => { setEditTrade(null); setQuickAddOpen(true) }}
              onImport={() => setImportOpen(true)}
              getTradeDisplayPnl={getTradeDisplayPnl}
              latestRates={latestRates}
              exchangeRates={exchangeRates}
              onOpenTradeReview={openTradeQuickReview}
              onAccountCashSaved={() => setTradeRefresh((value) => value + 1)}
            />
          )}
        </main>
      </div>
      {quickReviewTrade && (
        <TradeQuickReviewModal
          trade={quickReviewTrade}
          appearance="studio"
          onClose={() => setQuickReviewTrade(null)}
          onViewFullDetail={openTradeDetailPage}
          onEditTrade={openTradeEditor}
        />
      )}
      {quickAddOpen && (
        <QuickAddTradeModal
          appearance="studio"
          trade={editTrade}
          onClose={() => { setQuickAddOpen(false); setEditTrade(null) }}
          onSuccess={() => { setTradeRefresh((value) => value + 1); setEditTrade(null) }}
        />
      )}
      {importOpen && (
        <ImportTrades
          appearance="studio"
          onClose={() => setImportOpen(false)}
          onSuccess={() => {
            setImportOpen(false)
            setTradeRefresh((value) => value + 1)
          }}
        />
      )}
    </div>
  )
}

type TradeFilterKind = 'number' | 'date' | 'enum' | 'account' | 'strategy'

interface TradeFilterField {
  id: string
  label: string
  group: string
  kind: TradeFilterKind
  options?: string[]
}

interface TradeFieldFilter {
  id: string
  field: string
  op: string
  value: string
  valueTo: string
}

const TIMEFRAME_OPTIONS = ['1m', '5m', '15m', '30m', '1h', '4h', 'd', 'w', 'm']

const TRADE_FILTER_FIELDS: TradeFilterField[] = [
  { id: 'market', label: 'Market', group: 'Trade', kind: 'enum', options: ['Commodity', 'Crypto', 'Forex', 'Stock'] },
  { id: 'exchange', label: 'Exchange', group: 'Trade', kind: 'enum' },
  { id: 'account_id', label: 'Account', group: 'Trade', kind: 'account' },
  { id: 'direction', label: 'Direction', group: 'Trade', kind: 'enum', options: ['long', 'short'] },
  { id: 'status', label: 'Status', group: 'Trade', kind: 'enum', options: ['open', 'closed'] },
  { id: 'strategy_id', label: 'Strategy', group: 'Trade', kind: 'strategy' },
  { id: 'timeframe', label: 'Timeframe', group: 'Trade', kind: 'enum', options: TIMEFRAME_OPTIONS },
  { id: 'entry_date', label: 'Entry date', group: 'Dates', kind: 'date' },
  { id: 'exit_date', label: 'Exit date', group: 'Dates', kind: 'date' },
  { id: 'planned_rr', label: 'Planned RR', group: 'Performance', kind: 'number' },
  { id: 'realized_rr', label: 'Realized RR', group: 'Performance', kind: 'number' },
  { id: 'pl', label: 'P/L', group: 'Performance', kind: 'number' },
  { id: 'pl_pct', label: 'P/L %', group: 'Performance', kind: 'number' },
  { id: 'total_invested', label: 'Total Invested', group: 'Performance', kind: 'number' },
  { id: 'holding_days', label: 'Holding time (day)', group: 'Performance', kind: 'number' },
]

const FILTER_OPS: Record<TradeFilterKind, { id: string; label: string }[]> = {
  number: [{ id: 'gt', label: 'greater than' }, { id: 'lt', label: 'less than' }, { id: 'between', label: 'between' }],
  date: [{ id: 'before', label: 'before' }, { id: 'after', label: 'after' }, { id: 'between', label: 'between' }],
  enum: [{ id: 'eq', label: 'is' }],
  account: [{ id: 'eq', label: 'is' }],
  strategy: [{ id: 'eq', label: 'is' }],
}

function defaultFilterOp(kind: TradeFilterKind) {
  return FILTER_OPS[kind][0].id
}

function loadTradeFilters(): TradeFieldFilter[] {
  try {
    const stored = JSON.parse(localStorage.getItem('app-test-trade-filters') || '[]') as TradeFieldFilter[]
    if (!Array.isArray(stored)) return []
    return stored.flatMap((item) => {
      const field = TRADE_FILTER_FIELDS.find((entry) => entry.id === item.field)
      if (!field || !item.id) return []
      const op = FILTER_OPS[field.kind].some((entry) => entry.id === item.op) ? item.op : defaultFilterOp(field.kind)
      return [{ id: item.id, field: field.id, op, value: item.value || '', valueTo: item.valueTo || '' }]
    })
  } catch {
    return []
  }
}

function tradeDayKey(value: unknown) {
  if (!value) return ''
  const date = new Date(String(value))
  if (Number.isNaN(date.getTime())) return ''
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${date.getFullYear()}-${month}-${day}`
}

function openPositionSortValue(
  trade: DashboardTrade,
  columnId: OpenPositionSortKey,
  accounts: DashboardAccount[],
  displayPnl: (trade: DashboardTrade) => number,
) {
  const positionSize = trade.position_size ?? (trade.quantity != null ? Number(trade.quantity) * Number(trade.contract_size || 1) : null)
  const totalInvested = trade.total_entry_amount ?? (trade.quantity != null && trade.entry_price != null ? Number(trade.quantity) * Number(trade.entry_price) : null)
  const averageCostBasis = trade.cost_basis_remaining ?? (trade.long_short === 'long' ? trade.total_entry_amount : trade.total_exit_amount)
  const averageCost = trade.quantity && Number(trade.quantity) > 0 && averageCostBasis != null && Number(averageCostBasis) > 0
    ? Number(averageCostBasis) / Number(trade.quantity)
    : trade.entry_price
  const risk = trade.entry_price != null && trade.stop_loss != null
    ? Math.abs(Number(trade.entry_price) - Number(trade.stop_loss)) * Number(trade.quantity || 0) * Number(trade.contract_size || 1)
    : null
  const riskPercent = risk != null && totalInvested ? risk / Number(totalInvested) * 100 : null
  const pnl = displayPnl(trade)

  switch (columnId) {
    case 'instrument':
      return trade.symbol.toLowerCase()
    case 'side':
      return (trade.long_short || 'long').toLowerCase()
    case 'opened':
      return tradeDayKey(trade.entry_date)
    case 'entry':
      return trade.entry_price == null ? null : Number(trade.entry_price)
    case 'averageCost':
      return averageCost == null || Number(averageCost) <= 0 ? null : Number(averageCost)
    case 'positionSize':
      return positionSize == null ? null : Number(positionSize)
    case 'totalInvested':
      return totalInvested == null ? null : Number(totalInvested)
    case 'sl':
      return trade.stop_loss == null ? null : Number(trade.stop_loss)
    case 'tp':
      return trade.take_profit == null ? null : Number(trade.take_profit)
    case 'risk':
      return risk
    case 'riskPercent':
      return riskPercent
    case 'price':
      return trade.current_price == null ? null : Number(trade.current_price)
    case 'pnlPercent':
      return trade.unrealized_pnl_percentage == null ? null : Number(trade.unrealized_pnl_percentage)
    case 'pnl':
      return pnl
    case 'account':
      return (accounts.find((account) => account.id === trade.account_id)?.name || '').toLowerCase()
    case 'exchange':
      return (trade.exchange || '').toLowerCase()
    case 'tags':
      return trade.tags?.length ? trade.tags.join(', ').toLowerCase() : ''
    default:
      return null
  }
}

function holdingDays(trade: DashboardTrade) {
  const start = tradeDayKey(trade.entry_date)
  if (!start) return null
  const end = trade.exit_date ? tradeDayKey(trade.exit_date) : tradeDayKey(new Date().toISOString())
  if (!end) return null
  const elapsed = new Date(`${end}T12:00:00`).getTime() - new Date(`${start}T12:00:00`).getTime()
  return Math.round(elapsed / 86400000)
}

function realizedRr(trade: DashboardTrade) {
  const entry = trade.entry_price
  const exit = trade.exit_price
  const stop = trade.stop_loss
  if (entry == null || exit == null || stop == null) return null
  const short = (trade.long_short || '').toLowerCase() === 'short'
  const risk = short ? stop - entry : entry - stop
  if (risk <= 0) return null
  const reward = short ? entry - exit : exit - entry
  return reward / risk
}

function choiceLabel(fieldId: string, value: string) {
  if (fieldId === 'direction') return value === 'short' ? 'Short' : value === 'long' ? 'Long' : value
  if (fieldId === 'status') return value === 'closed' ? 'Closed' : value === 'open' ? 'Open' : value
  return value
}

function matchesTradeFilter(trade: DashboardTrade, filter: TradeFieldFilter, displayPnl: (trade: DashboardTrade) => number) {
  const field = TRADE_FILTER_FIELDS.find((entry) => entry.id === filter.field)
  if (!field) return true
  if (field.kind === 'number') {
    if (filter.op !== 'between' && filter.value.trim() === '') return true
    if (filter.op === 'between' && (filter.value.trim() === '' || filter.valueTo.trim() === '')) return true
    const actual = tradeFilterNumber(trade, field.id, displayPnl)
    const left = Number(filter.value)
    const right = Number(filter.valueTo)
    if (actual == null || Number.isNaN(actual) || Number.isNaN(left)) return false
    if (filter.op === 'gt') return actual > left
    if (filter.op === 'lt') return actual < left
    if (filter.op === 'between') return !Number.isNaN(right) && actual >= Math.min(left, right) && actual <= Math.max(left, right)
    return true
  }
  if (field.kind === 'date') {
    const actual = tradeDayKey(field.id === 'exit_date' ? trade.exit_date : trade.entry_date)
    if (!actual) return false
    if (filter.op === 'before') return !filter.value || actual < filter.value
    if (filter.op === 'after') return !filter.value || actual > filter.value
    if (filter.op === 'between') {
      if (!filter.value || !filter.valueTo) return true
      const [start, end] = [filter.value, filter.valueTo].sort()
      return actual >= start && actual <= end
    }
    return true
  }
  const needle = filter.value.trim().toLowerCase()
  if (!needle) return true
  const haystack = tradeFilterChoice(trade, field.id).toLowerCase()
  return haystack === needle
}

function tradeFilterNumber(trade: DashboardTrade, fieldId: string, displayPnl: (trade: DashboardTrade) => number) {
  if (fieldId === 'planned_rr') return trade.rr == null ? null : Number(trade.rr)
  if (fieldId === 'realized_rr') return realizedRr(trade)
  if (fieldId === 'pl') return displayPnl(trade)
  if (fieldId === 'pl_pct') {
    const value = trade.exit_date ? trade.pnl_percentage : trade.unrealized_pnl_percentage
    return value == null ? null : Number(value)
  }
  if (fieldId === 'total_invested') return trade.total_entry_amount == null ? null : Number(trade.total_entry_amount)
  if (fieldId === 'holding_days') return holdingDays(trade)
  return null
}

function tradeFilterChoice(trade: DashboardTrade, fieldId: string) {
  if (fieldId === 'market') return trade.market || ''
  if (fieldId === 'exchange') return trade.exchange || ''
  if (fieldId === 'account_id') return trade.account_id || ''
  if (fieldId === 'direction') return (trade.long_short || '').toLowerCase()
  if (fieldId === 'status') return trade.exit_date ? 'closed' : 'open'
  if (fieldId === 'strategy_id') return trade.strategy_id || ''
  if (fieldId === 'timeframe') return trade.time_interval || ''
  return ''
}

function journalSortValue(
  trade: DashboardTrade,
  columnId: JournalColumnId,
  accounts: DashboardAccount[],
  strategies: DashboardStrategy[],
  displayPnl: (trade: DashboardTrade) => number,
) {
  switch (columnId) {
    case 'instrument':
      return trade.symbol.toLowerCase()
    case 'market':
      return (trade.market || '').toLowerCase()
    case 'exchange':
      return (trade.exchange || '').toLowerCase()
    case 'account':
      return (accounts.find((account) => account.id === trade.account_id)?.name || '').toLowerCase()
    case 'direction':
      return (trade.long_short || 'long').toLowerCase()
    case 'status':
      return trade.exit_date ? 'closed' : 'open'
    case 'strategy':
      return (strategies.find((strategy) => strategy.id === trade.strategy_id)?.display_name || '').toLowerCase()
    case 'timeframe':
      return (trade.time_interval || '').toLowerCase()
    case 'entry':
      return tradeDayKey(trade.entry_date)
    case 'exit':
      return tradeDayKey(trade.exit_date)
    case 'planned_rr':
      return trade.rr == null ? null : Number(trade.rr)
    case 'realized_rr':
      return realizedRr(trade)
    case 'pl':
      return displayPnl(trade)
    case 'pl_pct': {
      const value = trade.exit_date ? trade.pnl_percentage : trade.unrealized_pnl_percentage
      return value == null ? null : Number(value)
    }
    case 'total_invested':
      return trade.total_entry_amount == null ? null : Number(trade.total_entry_amount)
    case 'holding_days':
      return holdingDays(trade)
    default:
      return ''
  }
}

function TradeJournal({
  trades,
  accounts,
  strategies,
  currency,
  onAddTrade,
  onImport,
  onOpenTradeReview,
  getTradeDisplayPnl,
}: {
  trades: DashboardTrade[]
  accounts: DashboardAccount[]
  strategies: DashboardStrategy[]
  currency: string
  onAddTrade: () => void
  onImport: () => void
  onOpenTradeReview: (trade: DashboardTrade, event?: React.MouseEvent) => void
  getTradeDisplayPnl: (trade: DashboardTrade) => number
}) {
  const [filters, setFilters] = useState<TradeFieldFilter[]>(loadTradeFilters)
  const [journalColumns, setJournalColumns] = useState<JournalColumnId[]>(loadJournalColumns)
  const [journalColumnDraft, setJournalColumnDraft] = useState<{ id: JournalColumnId; visible: boolean }[]>([])
  const [showJournalColumnsEditor, setShowJournalColumnsEditor] = useState(false)
  const [sort, setSort] = useState<{ id: JournalColumnId; dir: 'asc' | 'desc' } | null>(null)
  const [pickerOpen, setPickerOpen] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [fieldQuery, setFieldQuery] = useState('')
  const [activeGroup, setActiveGroup] = useState<string | null>(null)
  const accountNameById = useMemo(() => new Map(accounts.map((account) => [account.id, account.name])), [accounts])
  const strategyNameById = useMemo(() => new Map(strategies.map((strategy) => [strategy.id, strategy.display_name])), [strategies])
  useEffect(() => {
    localStorage.setItem('app-test-trade-filters', JSON.stringify(filters))
  }, [filters])
  useEffect(() => {
    if (!pickerOpen && !editingId) return
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return
      setPickerOpen(false)
      setEditingId(null)
      setActiveGroup(null)
      setFieldQuery('')
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [pickerOpen, editingId])

  const filtered = trades.filter((trade) => filters.every((filter) => matchesTradeFilter(trade, filter, getTradeDisplayPnl)))
  const tableRows = useMemo(() => {
    if (!sort) return filtered
    const direction = sort.dir === 'asc' ? 1 : -1
    return [...filtered].sort((left, right) => {
      const a = journalSortValue(left, sort.id, accounts, strategies, getTradeDisplayPnl)
      const b = journalSortValue(right, sort.id, accounts, strategies, getTradeDisplayPnl)
      const aEmpty = a == null || a === ''
      const bEmpty = b == null || b === ''
      if (aEmpty && bEmpty) return 0
      if (aEmpty) return 1
      if (bEmpty) return -1
      if (typeof a === 'number' && typeof b === 'number') return (a - b) * direction
      return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' }) * direction
    })
  }, [filtered, sort, accounts, strategies, getTradeDisplayPnl])
  const closed = filtered.filter((trade) => trade.exit_date)
  const open = filtered.filter((trade) => !trade.exit_date)
  const netPnl = closed.reduce((sum, trade) => sum + getTradeDisplayPnl(trade), 0)
  const openPnl = open.reduce((sum, trade) => sum + getTradeDisplayPnl(trade), 0)
  const query = fieldQuery.trim().toLowerCase()
  const groupIcons: Record<string, string> = {
    Trade: 'trades',
    Dates: 'columns',
    Performance: 'analytics',
  }
  const choiceOptions = (field: TradeFilterField) => {
    if (field.id === 'exchange') {
      return Array.from(new Set(trades.map((trade) => trade.exchange).filter((exchange): exchange is string => Boolean(exchange)))).sort()
    }
    if (field.id === 'timeframe') {
      const used = trades.map((trade) => trade.time_interval).filter((value): value is string => Boolean(value))
      return Array.from(new Set([...(field.options || []), ...used]))
    }
    return field.options || []
  }
  const groups = Array.from(new Set(TRADE_FILTER_FIELDS.map((field) => field.group))).map((group) => ({
    group,
    count: TRADE_FILTER_FIELDS.filter((field) => field.group === group).length,
  }))
  const visibleFields = TRADE_FILTER_FIELDS.filter((field) => {
    const matchesQuery = !query || `${field.label} ${field.group} ${field.id}`.toLowerCase().includes(query)
    const matchesGroup = !query && activeGroup ? field.group === activeGroup : true
    return matchesQuery && matchesGroup
  })
  const editing = filters.find((filter) => filter.id === editingId) || null
  const editingField = TRADE_FILTER_FIELDS.find((field) => field.id === editing?.field)

  const closePicker = () => {
    setPickerOpen(false)
    setActiveGroup(null)
    setFieldQuery('')
  }

  const addFilter = (field: TradeFilterField) => {
    const id = `${field.id}-${Date.now()}`
    setFilters((current) => [...current, {
      id,
      field: field.id,
      op: defaultFilterOp(field.kind),
      value: '',
      valueTo: '',
    }])
    closePicker()
    setEditingId(id)
  }

  const updateFilter = (id: string, patch: Partial<TradeFieldFilter>) => {
    setFilters((current) => current.map((filter) => filter.id === id ? { ...filter, ...patch } : filter))
  }

  const openJournalColumnsEditor = () => {
    const visible = new Set(journalColumns)
    setJournalColumnDraft([
      ...journalColumns.map((id) => ({ id, visible: true })),
      ...JOURNAL_TABLE_COLUMNS.filter((column) => !visible.has(column.id)).map((column) => ({ id: column.id, visible: false })),
    ])
    setShowJournalColumnsEditor(true)
  }

  const moveJournalColumn = (index: number, direction: -1 | 1) => {
    setJournalColumnDraft((current) => {
      const target = index + direction
      if (target < 0 || target >= current.length) return current
      const next = [...current]
      ;[next[index], next[target]] = [next[target], next[index]]
      return next
    })
  }

  const saveJournalColumns = () => {
    const selected = journalColumnDraft.filter((column) => column.visible).map((column) => column.id)
    if (!selected.length) return
    setJournalColumns(selected)
    localStorage.setItem('app-test-journal-columns', JSON.stringify(selected))
    setShowJournalColumnsEditor(false)
  }

  const toggleSort = (columnId: JournalColumnId) => {
    setSort((current) => {
      if (!current || current.id !== columnId) return { id: columnId, dir: 'asc' }
      if (current.dir === 'asc') return { id: columnId, dir: 'desc' }
      return null
    })
  }

  const renderJournalCell = (trade: DashboardTrade, columnId: JournalColumnId) => {
    const tradeCurrency = trade.currency || currency
    const pnl = getTradeDisplayPnl(trade)
    const plPct = trade.exit_date ? trade.pnl_percentage : trade.unrealized_pnl_percentage
    switch (columnId) {
      case 'instrument':
        return <><SymbolLogo symbol={trade.symbol} market={trade.market} exchange={trade.exchange} size={20} /><strong>{trade.symbol}</strong></>
      case 'market':
        return trade.market || '—'
      case 'exchange':
        return trade.exchange || '—'
      case 'account':
        return accountNameById.get(trade.account_id || '') || '—'
      case 'direction':
        return <span className={`at-side ${trade.long_short === 'short' ? 'short' : ''}`}>{trade.long_short || 'long'}</span>
      case 'status':
        return <span className={`at-status ${trade.exit_date ? '' : 'open'}`}>{trade.exit_date ? 'Closed' : 'Open'}</span>
      case 'strategy':
        return strategyNameById.get(trade.strategy_id || '') || '—'
      case 'timeframe':
        return trade.time_interval || '—'
      case 'entry':
        return formatDate(trade.entry_date)
      case 'exit':
        return formatDate(trade.exit_date)
      case 'planned_rr':
        return trade.rr != null ? Number(trade.rr).toFixed(2) : '—'
      case 'realized_rr': {
        const value = realizedRr(trade)
        return value == null ? '—' : value.toFixed(2)
      }
      case 'pl':
        return <span className={pnl >= 0 ? 'positive' : 'negative'}>{pnl >= 0 ? '+' : ''}{formatMoney(pnl, currency)}</span>
      case 'pl_pct':
        return plPct == null ? '—' : `${Number(plPct) >= 0 ? '+' : ''}${Number(plPct).toFixed(2)}%`
      case 'total_invested':
        return trade.total_entry_amount != null ? formatMoney(Number(trade.total_entry_amount), tradeCurrency) : '—'
      case 'holding_days': {
        const days = holdingDays(trade)
        return days == null ? '—' : String(days)
      }
      default:
        return '—'
    }
  }

  const filterLabel = (filter: TradeFieldFilter) => {
    const field = TRADE_FILTER_FIELDS.find((entry) => entry.id === filter.field)
    if (!field) return 'Filter'
    const op = FILTER_OPS[field.kind].find((entry) => entry.id === filter.op)?.label || filter.op
    let shown = filter.value
    if (field.kind === 'account') shown = accounts.find((account) => account.id === filter.value)?.name || ''
    if (field.kind === 'strategy') shown = strategies.find((strategy) => strategy.id === filter.value)?.display_name || ''
    if (field.kind === 'enum') shown = choiceLabel(field.id, filter.value)
    if (filter.op === 'between') shown = filter.value && filter.valueTo ? `${filter.value} – ${filter.valueTo}` : ''
    return shown ? `+${field.label} (${op} ${shown})` : `+${field.label}`
  }

  return (
    <section className="at-subpage at-trades-page">
      <div className="at-subpage-kpis">
        <MetricCard label="Journal entries" context="Matching filters" value={String(filtered.length)} progress={Math.min(filtered.length * 4, 100)} tone="blue" footer={`${closed.length} closed · ${open.length} open`} />
        <MetricCard label="Net P/L" context="Realized" value={formatMoney(netPnl, currency)} progress={Math.min(Math.abs(netPnl) / 20, 100)} tone={netPnl >= 0 ? 'mint' : 'rose'} footer="Closed trades in this view" />
        <MetricCard label="Open positions" context="Live journal" value={String(open.length)} progress={Math.min(open.length * 15, 100)} tone="amber" footer={formatMoney(openPnl, currency) + ' unrealized'} />
      </div>
      <div className="at-filter-bar">
        <div className="at-filter-chips">
          {filters.map((filter) => (
            <span className="at-filter-chip" key={filter.id}>
              <button type="button" onClick={() => setEditingId(filter.id)}>{filterLabel(filter)}</button>
              <button type="button" aria-label={`Remove ${filterLabel(filter)}`} onClick={() => setFilters((current) => current.filter((item) => item.id !== filter.id))}>×</button>
            </span>
          ))}
        </div>
        <span className="at-filter-bar-actions">
          <button className="at-filter-add" onClick={() => { setEditingId(null); setPickerOpen(true) }}>+ Add filter</button>
          {filters.length > 0 && <button className="at-filter-clear" onClick={() => setFilters([])}>Clear all</button>}
        </span>
      </div>
      <article className="at-card at-subpage-panel">
        <div className="at-section-heading">
          <div><strong>Trading journal</strong><span>{filtered.length} matching entries</span></div>
          <span className="at-table-heading-actions">
            <button onClick={openJournalColumnsEditor} title="Edit table columns"><Icon name="columns" size={13} /> Edit columns</button>
            <button onClick={onImport} title="Import trades"><Icon name="upload" size={13} /> Import</button>
            <button className="at-primary-small" onClick={onAddTrade}><Icon name="add" size={13} /> Add trade</button>
          </span>
        </div>
        {pickerOpen && (
          <div className="at-filter-modal-backdrop" role="dialog" aria-modal="true" aria-label="Add filter" onMouseDown={closePicker}>
            <div className="at-filter-modal" onMouseDown={(event) => event.stopPropagation()}>
              <header>
                <strong>Add filter</strong>
                <button type="button" onClick={closePicker} aria-label="Close"><Icon name="close" size={16} /></button>
              </header>
              <p>{query || activeGroup ? 'Choose a field.' : 'Pick a category or search for a filter.'}</p>
              <div className="at-filter-modal-search">
                <input value={fieldQuery} onChange={(event) => setFieldQuery(event.target.value)} placeholder="Search" autoFocus />
              </div>
              <div className="at-filter-modal-list">
                {!query && activeGroup && (
                  <button className="at-filter-back" onClick={() => setActiveGroup(null)}>← {activeGroup}</button>
                )}
                {!query && !activeGroup ? groups.map((group) => (
                  <button key={group.group} onClick={() => setActiveGroup(group.group)}>
                    <Icon name={groupIcons[group.group] || 'filter'} size={15} />
                    <span>{group.group}</span>
                    <em>{group.count}</em>
                  </button>
                )) : visibleFields.map((field) => (
                  <button key={field.id} onClick={() => addFilter(field)}>
                    <span>{field.label}<small>{field.group}</small></span>
                  </button>
                ))}
                {query && !visibleFields.length && <p className="at-filter-empty">No fields match “{fieldQuery.trim()}”.</p>}
              </div>
              <footer><button type="button" onClick={closePicker}>Cancel</button></footer>
            </div>
          </div>
        )}
        {editing && editingField && (
          <div className="at-filter-modal-backdrop" role="dialog" aria-modal="true" aria-label={`Edit ${editingField.label} filter`} onMouseDown={() => setEditingId(null)}>
            <div className="at-filter-modal at-filter-editor" onMouseDown={(event) => event.stopPropagation()}>
              <header>
                <strong>{editingField.label}</strong>
                <button type="button" onClick={() => setEditingId(null)} aria-label="Close"><Icon name="close" size={16} /></button>
              </header>
              <p>Update the condition for this filter.</p>
              <label>
                Condition
                <select value={editing.op} onChange={(event) => updateFilter(editing.id, { op: event.target.value })}>
                  {FILTER_OPS[editingField.kind].map((op) => <option key={op.id} value={op.id}>{op.label}</option>)}
                </select>
              </label>
              <div className="at-filter-editor-values">
                  {editingField.kind === 'enum' && (
                    <select value={editing.value} onChange={(event) => updateFilter(editing.id, { value: event.target.value })}>
                      <option value="">Any</option>
                      {choiceOptions(editingField).map((option) => <option key={option} value={option}>{choiceLabel(editingField.id, option)}</option>)}
                    </select>
                  )}
                  {editingField.kind === 'account' && (
                    <select value={editing.value} onChange={(event) => updateFilter(editing.id, { value: event.target.value })}>
                      <option value="">Any account</option>
                      {accounts.map((account) => <option key={account.id} value={account.id}>{account.name}</option>)}
                    </select>
                  )}
                  {editingField.kind === 'strategy' && (
                    <select value={editing.value} onChange={(event) => updateFilter(editing.id, { value: event.target.value })}>
                      <option value="">Any strategy</option>
                      {strategies.map((strategy) => <option key={strategy.id} value={strategy.id}>{strategy.display_name}</option>)}
                    </select>
                  )}
                  {editingField.kind === 'number' && (
                    <>
                      <input type="number" step="any" value={editing.value} onChange={(event) => updateFilter(editing.id, { value: event.target.value })} placeholder="Value" />
                      {editing.op === 'between' && <input type="number" step="any" value={editing.valueTo} onChange={(event) => updateFilter(editing.id, { valueTo: event.target.value })} placeholder="And" />}
                    </>
                  )}
                  {editingField.kind === 'date' && (
                    <>
                      <input type="date" value={editing.value} onChange={(event) => updateFilter(editing.id, { value: event.target.value })} />
                      {editing.op === 'between' && <input type="date" value={editing.valueTo} onChange={(event) => updateFilter(editing.id, { valueTo: event.target.value })} />}
                    </>
                  )}
                </div>
              <footer><button type="button" onClick={() => setEditingId(null)}>Done</button></footer>
            </div>
          </div>
        )}
        {showJournalColumnsEditor && (
          <div className="at-columns-modal-backdrop" role="dialog" aria-modal="true" aria-label="Edit journal table columns" onClick={() => setShowJournalColumnsEditor(false)}>
            <div className="at-columns-modal" onClick={(event) => event.stopPropagation()}>
              <div className="at-columns-modal-title">
                <span><strong>Journal columns</strong><small>Show, hide, and reorder table columns.</small></span>
                <button type="button" onClick={() => setShowJournalColumnsEditor(false)} aria-label="Close"><Icon name="close" size={16} /></button>
              </div>
              <div className="at-columns-modal-list">
                {journalColumnDraft.map((column, index) => {
                  const meta = JOURNAL_TABLE_COLUMNS.find((entry) => entry.id === column.id)!
                  return (
                    <div key={column.id}>
                      <label>
                        <input type="checkbox" checked={column.visible} onChange={() => setJournalColumnDraft((current) => current.map((item, itemIndex) => itemIndex === index ? { ...item, visible: !item.visible } : item))} />
                        {meta.label}
                      </label>
                      <span>
                        <button type="button" disabled={index === 0} onClick={() => moveJournalColumn(index, -1)} aria-label="Move column up">↑</button>
                        <button type="button" disabled={index === journalColumnDraft.length - 1} onClick={() => moveJournalColumn(index, 1)} aria-label="Move column down">↓</button>
                      </span>
                    </div>
                  )
                })}
              </div>
              <div className="at-columns-modal-actions">
                <button type="button" onClick={() => setShowJournalColumnsEditor(false)}>Cancel</button>
                <button type="button" className="primary" onClick={saveJournalColumns} disabled={!journalColumnDraft.some((column) => column.visible)}>Save columns</button>
              </div>
            </div>
          </div>
        )}
        {filtered.length ? (
          <div className="at-data-table at-journal-table">
            <table>
              <thead>
                <tr>
                  {journalColumns.map((columnId) => {
                    const meta = JOURNAL_TABLE_COLUMNS.find((column) => column.id === columnId)!
                    const active = sort?.id === columnId
                    return (
                      <th key={columnId}>
                        <button type="button" className={`at-th-sort ${active ? 'active' : ''}`} onClick={() => toggleSort(columnId)}>
                          {meta.label}
                          <span aria-hidden="true">{active ? (sort?.dir === 'asc' ? '↑' : '↓') : '↕'}</span>
                        </button>
                      </th>
                    )
                  })}
                </tr>
              </thead>
              <tbody>
                {tableRows.map((trade) => (
                  <tr key={trade.id} onClick={(event) => onOpenTradeReview(trade, event)}>
                    {journalColumns.map((columnId) => (
                      <td key={columnId} className={columnId === 'instrument' ? 'at-cell-instrument' : columnId === 'pl' ? 'at-cell-pl' : undefined}>
                        {renderJournalCell(trade, columnId)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : <EmptyState title="No matching trades" detail="Change the filters, search, or reporting range." />}
      </article>
    </section>
  )
}

function AccountCashModal({
  account,
  type,
  transaction,
  userId,
  onClose,
  onSaved,
}: {
  account: DashboardAccount
  type: ManualCashType
  transaction?: AccountTransaction | null
  userId: string
  onClose: () => void
  onSaved: () => void
}) {
  const isEdit = Boolean(transaction)
  const isOpeningEdit = transaction?.source === 'opening'
  const cashType: ManualCashType = transaction ? ledgerCashModalType(transaction) : type
  const [amount, setAmount] = useState('')
  const [occurredAt, setOccurredAt] = useState(() => toDateTimeLocal(new Date()))
  const [note, setNote] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const accountCurrency = account.currency || 'USD'

  useEffect(() => {
    if (!transaction) {
      setAmount('')
      setOccurredAt(toDateTimeLocal(new Date()))
      setNote('')
      setError(null)
      return
    }
    if (transaction.type === 'adjustment') {
      setAmount(String(Number(transaction.amount)))
    } else {
      const magnitude = transaction.original_amount != null
        ? Number(transaction.original_amount)
        : Math.abs(Number(transaction.amount))
      setAmount(String(magnitude))
    }
    setOccurredAt(toDateTimeLocal(new Date(transaction.occurred_at)))
    setNote(transaction.note || '')
    setError(null)
  }, [transaction])

  const handleSave = async () => {
    const when = new Date(occurredAt)
    if (isNaN(when.getTime())) {
      setError('Enter a valid date.')
      return
    }

    let parsedInitialBalance: number | null = null
    let raw = 0
    if (isOpeningEdit) {
      if (amount.trim() === '') {
        parsedInitialBalance = null
      } else {
        raw = parseFloat(amount.replace(',', '.'))
        if (isNaN(raw) || raw < 0) {
          setError('Enter a valid opening balance (zero or positive).')
          return
        }
        parsedInitialBalance = raw
      }
    } else {
      raw = parseFloat(amount.replace(',', '.'))
      if (isNaN(raw) || raw === 0) {
        setError(cashType === 'adjustment' ? 'Enter a non-zero amount (use + or −).' : 'Enter an amount greater than zero.')
        return
      }
      if (cashType !== 'adjustment' && raw <= 0) {
        setError('Enter an amount greater than zero.')
        return
      }
    }

    const signedAmount = cashType === 'deposit' ? raw : cashType === 'withdrawal' ? -raw : raw
    const originalAmount = cashType === 'adjustment' ? Math.abs(raw) : raw

    setSaving(true)
    setError(null)
    try {
      if (isEdit && transaction && isOpeningEdit) {
        const { error: accountError } = await supabase
          .from('accounts')
          .update({
            initial_balance: parsedInitialBalance,
            updated_at: new Date().toISOString(),
          })
          .eq('id', account.id)
          .eq('user_id', userId)
        if (accountError) throw accountError

        if (parsedInitialBalance != null) {
          const { error: openingTxError } = await supabase
            .from('account_transactions')
            .update({
              occurred_at: when.toISOString(),
              note: note.trim() || 'Opening balance',
            })
            .eq('id', transaction.id)
            .eq('user_id', userId)
            .eq('source', 'opening')
          if (openingTxError) throw openingTxError
        }
      } else if (isEdit && transaction) {
        const { error: updateError } = await supabase
          .from('account_transactions')
          .update({
            type: cashType,
            amount: signedAmount,
            original_amount: originalAmount,
            original_currency: accountCurrency,
            fx_rate: null,
            occurred_at: when.toISOString(),
            note: note.trim() || null,
          })
          .eq('id', transaction.id)
          .eq('user_id', userId)
          .eq('source', 'manual')
        if (updateError) throw updateError
      } else {
        const { error: insertError } = await supabase.from('account_transactions').insert({
          account_id: account.id,
          user_id: userId,
          type: cashType,
          amount: signedAmount,
          original_amount: originalAmount,
          original_currency: accountCurrency,
          fx_rate: null,
          occurred_at: when.toISOString(),
          note: note.trim() || null,
          source: 'manual',
        })
        if (insertError) throw insertError
      }
      onSaved()
      onClose()
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to save transaction')
    } finally {
      setSaving(false)
    }
  }

  const titleLabel = isOpeningEdit
    ? (isEdit ? 'Edit opening balance' : 'Opening balance')
    : (isEdit ? `Edit ${MANUAL_CASH_LABEL[cashType]}` : MANUAL_CASH_LABEL[cashType])

  return (
    <div className="at-filter-modal-backdrop" role="dialog" aria-modal="true" onClick={onClose}>
      <div className="at-filter-modal at-cash-modal" onClick={(event) => event.stopPropagation()}>
        <header>
          <strong>{titleLabel} · {account.name}</strong>
          <button type="button" onClick={onClose} aria-label="Close"><Icon name="close" size={17} /></button>
        </header>
        <p>
          {isOpeningEdit
            ? `Updates the account opening balance in ${accountCurrency}. Clear the amount to remove the opening entry.`
            : cashType === 'adjustment'
              ? 'Signed amount in account currency (e.g. −50 to reduce cash).'
              : isEdit
                ? `Update this ${MANUAL_CASH_LABEL[cashType].toLowerCase()} in ${accountCurrency}.`
                : `Record a ${MANUAL_CASH_LABEL[cashType].toLowerCase()} in ${accountCurrency}.`}
        </p>
        <div className="at-cash-modal-fields">
          <label>
            {isOpeningEdit ? `Opening balance (${accountCurrency})` : `Amount (${accountCurrency})`}
            <input
              type="number"
              step="0.01"
              min={isOpeningEdit ? '0' : undefined}
              value={amount}
              onChange={(event) => setAmount(event.target.value)}
              placeholder={isOpeningEdit ? 'e.g. 10000' : cashType === 'adjustment' ? 'e.g. -50 or 25' : '0.00'}
              autoFocus
            />
          </label>
          <label>
            Date
            <input type="datetime-local" value={occurredAt} onChange={(event) => setOccurredAt(event.target.value)} />
          </label>
          <label>
            Note
            <input type="text" value={note} onChange={(event) => setNote(event.target.value)} placeholder="Optional" />
          </label>
        </div>
        {error && <p className="at-cash-modal-error">{error}</p>}
        <footer>
          <button type="button" onClick={onClose}>Cancel</button>
          <button type="button" className="at-cash-modal-save" disabled={saving} onClick={handleSave}>
            {saving ? 'Saving…' : 'Save'}
          </button>
        </footer>
      </div>
    </div>
  )
}

type LedgerSortColumn = 'date' | 'type' | 'note' | 'original' | 'fx' | 'cash' | 'balance'

function ledgerSortValue(row: AccountLedgerRow, column: LedgerSortColumn) {
  switch (column) {
    case 'date':
      return new Date(row.occurred_at).getTime()
    case 'type':
      return TX_TYPE_LABEL[row.type].toLowerCase()
    case 'note':
      return (row.note || '').toLowerCase()
    case 'original':
      return row.original_amount != null ? Number(row.original_amount) : Number.NEGATIVE_INFINITY
    case 'fx':
      return row.fx_rate != null ? Number(row.fx_rate) : Number.NEGATIVE_INFINITY
    case 'cash':
      return Number(row.amount)
    case 'balance':
      return row.runningBalance
    default:
      return 0
  }
}

function AccountLedgerTable({
  account,
  accountId,
  rows,
  accountCurrency,
  allTrades,
  userId,
  onOpenTradeReview,
  onSaved,
}: {
  account: DashboardAccount
  accountId: string
  rows: AccountLedgerRow[]
  accountCurrency: string
  allTrades: DashboardTrade[]
  userId: string
  onOpenTradeReview: (trade: DashboardTrade, event?: React.MouseEvent) => void
  onSaved: () => void
}) {
  const [editingTransaction, setEditingTransaction] = useState<AccountTransaction | null>(null)
  const [noteSearch, setNoteSearch] = useState('')
  const [typeFilter, setTypeFilter] = useState<AccountTxType | 'all'>('all')
  const [sourceFilter, setSourceFilter] = useState<AccountTransaction['source'] | 'all'>('all')
  const [sort, setSort] = useState<{ id: LedgerSortColumn; dir: 'asc' | 'desc' }>({ id: 'date', dir: 'asc' })

  useEffect(() => {
    setNoteSearch('')
    setTypeFilter('all')
    setSourceFilter('all')
    setSort({ id: 'date', dir: 'asc' })
    setEditingTransaction(null)
  }, [accountId])

  const typesInLedger = useMemo(
    () => Array.from(new Set(rows.map((row) => row.type))).sort((a, b) => TX_TYPE_LABEL[a].localeCompare(TX_TYPE_LABEL[b])),
    [rows]
  )

  const filteredRows = useMemo(() => {
    const query = noteSearch.trim().toLowerCase()
    return rows.filter((row) => {
      if (typeFilter !== 'all' && row.type !== typeFilter) return false
      if (sourceFilter !== 'all' && row.source !== sourceFilter) return false
      if (!query) return true
      const haystack = `${row.note || ''} ${TX_TYPE_LABEL[row.type]}`.toLowerCase()
      return haystack.includes(query)
    })
  }, [rows, noteSearch, typeFilter, sourceFilter])

  const displayRows = useMemo(() => {
    const next = [...filteredRows]
    next.sort((left, right) => {
      const a = ledgerSortValue(left, sort.id)
      const b = ledgerSortValue(right, sort.id)
      if (typeof a === 'string' && typeof b === 'string') {
        const cmp = a.localeCompare(b)
        return sort.dir === 'asc' ? cmp : -cmp
      }
      const cmp = Number(a) - Number(b)
      return sort.dir === 'asc' ? cmp : -cmp
    })
    return next
  }, [filteredRows, sort])

  const toggleSort = (columnId: LedgerSortColumn) => {
    setSort((current) => {
      if (current.id !== columnId) return { id: columnId, dir: 'asc' }
      if (current.dir === 'asc') return { id: columnId, dir: 'desc' }
      return { id: 'date', dir: 'asc' }
    })
  }

  const filtersActive = Boolean(noteSearch.trim()) || typeFilter !== 'all' || sourceFilter !== 'all'

  const clearFilters = () => {
    setNoteSearch('')
    setTypeFilter('all')
    setSourceFilter('all')
  }

  const ledgerColumns: { id: LedgerSortColumn; label: string }[] = [
    { id: 'date', label: 'Date' },
    { id: 'type', label: 'Type' },
    { id: 'note', label: 'Note' },
    { id: 'original', label: 'Original' },
    { id: 'fx', label: 'FX' },
    { id: 'cash', label: 'Cash' },
    { id: 'balance', label: 'Balance' },
  ]

  if (!rows.length) {
    return <EmptyState title="No cash activity" detail="Deposits, withdrawals, and trade fills will appear here." />
  }

  return (
    <>
      <div className="at-ledger-toolbar">
        <label className="at-ledger-search">
          <Icon name="search" size={14} />
          <input
            type="search"
            value={noteSearch}
            onChange={(event) => setNoteSearch(event.target.value)}
            placeholder="Search note or type…"
            aria-label="Search ledger"
          />
        </label>
        <label className="at-ledger-filter">
          Type
          <select value={typeFilter} onChange={(event) => setTypeFilter(event.target.value as AccountTxType | 'all')}>
            <option value="all">All types</option>
            {typesInLedger.map((type) => (
              <option key={type} value={type}>{TX_TYPE_LABEL[type]}</option>
            ))}
          </select>
        </label>
        <label className="at-ledger-filter">
          Source
          <select value={sourceFilter} onChange={(event) => setSourceFilter(event.target.value as AccountTransaction['source'] | 'all')}>
            <option value="all">All sources</option>
            <option value="trade">Trades</option>
            <option value="manual">Manual</option>
            <option value="opening">Opening</option>
          </select>
        </label>
        {filtersActive && (
          <button type="button" className="at-ledger-clear" onClick={clearFilters}>Clear filters</button>
        )}
        <span className="at-ledger-filter-count">
          {displayRows.length === rows.length ? `${rows.length} rows` : `${displayRows.length} of ${rows.length}`}
        </span>
      </div>
      {displayRows.length ? (
        <div className="at-data-table at-ledger-table">
          <table>
            <thead>
              <tr>
                {ledgerColumns.map((column) => {
                  const active = sort.id === column.id
                  return (
                    <th key={column.id}>
                      <button
                        type="button"
                        className={`at-th-sort ${active ? 'active' : ''}`}
                        onClick={() => toggleSort(column.id)}
                      >
                        {column.label}
                        <span aria-hidden="true">{active ? (sort.dir === 'asc' ? '↑' : '↓') : '↕'}</span>
                      </button>
                    </th>
                  )
                })}
                <th className="at-ledger-actions-col" aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {displayRows.map((row) => {
                const originalCurrency = row.original_currency || accountCurrency
                const showFx = row.fx_rate != null && originalCurrency !== accountCurrency
                const editable = isEditableLedgerRow(row)
                return (
                  <tr
                    key={row.id}
                    className={row.trade_id ? 'clickable' : editable ? 'editable' : ''}
                    onClick={() => {
                      if (row.trade_id) {
                        const trade = allTrades.find((entry) => entry.id === row.trade_id)
                        if (trade) onOpenTradeReview(trade)
                        return
                      }
                      if (editable) setEditingTransaction(row)
                    }}
                  >
                    <td>{formatDateTime(row.occurred_at)}</td>
                    <td><span className={`at-tx-type ${row.type}`}>{TX_TYPE_LABEL[row.type]}</span></td>
                    <td className="at-ledger-note" title={row.note || undefined}>{row.note || '—'}</td>
                    <td>{row.original_amount != null ? formatMoney(Number(row.original_amount), originalCurrency) : '—'}</td>
                    <td>{showFx ? Number(row.fx_rate).toFixed(4) : '—'}</td>
                    <td className={Number(row.amount) >= 0 ? 'positive' : 'negative'}>
                      {Number(row.amount) >= 0 ? '+' : ''}{formatMoney(Number(row.amount), accountCurrency)}
                    </td>
                    <td>{formatMoney(row.runningBalance, accountCurrency)}</td>
                    <td className="at-ledger-actions-col">
                      {editable && (
                        <button
                          type="button"
                          className="at-ledger-edit"
                          onClick={(event) => {
                            event.stopPropagation()
                            setEditingTransaction(row)
                          }}
                        >
                          Edit
                        </button>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <EmptyState title="No matching rows" detail="Try another type, source, or search term." action={
          <button type="button" className="at-ledger-clear" onClick={clearFilters}>Clear filters</button>
        } />
      )}
      {editingTransaction && (
        <AccountCashModal
          account={account}
          type={ledgerCashModalType(editingTransaction)}
          transaction={editingTransaction}
          userId={userId}
          onClose={() => setEditingTransaction(null)}
          onSaved={() => {
            onSaved()
            setEditingTransaction(null)
          }}
        />
      )}
    </>
  )
}

function AccountFormModal({
  userId,
  account,
  accountCount,
  maxAccounts,
  onClose,
  onSaved,
}: {
  userId: string
  account: DashboardAccount | null
  accountCount: number
  maxAccounts: number | null
  onClose: () => void
  onSaved: () => void
}) {
  const { t } = useTranslation(['common', 'accounts'])
  const isEdit = Boolean(account)
  const [name, setName] = useState(account?.name || '')
  const [description, setDescription] = useState(account?.description || '')
  const [formCurrency, setFormCurrency] = useState(account?.currency || 'USD')
  const [platform, setPlatform] = useState(account?.platform || '')
  const [initialBalance, setInitialBalance] = useState(
    account?.initial_balance != null ? String(account.initial_balance) : ''
  )
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [feeEnabledScopes, setFeeEnabledScopes] = useState<string[]>([])
  const [formFeeForms, setFormFeeForms] = useState<Record<string, AccountFeeRuleForm>>(() => emptyFeeRuleFormMap())

  useEffect(() => {
    setName(account?.name || '')
    setDescription(account?.description || '')
    setFormCurrency(account?.currency || 'USD')
    setPlatform(account?.platform || '')
    setInitialBalance(account?.initial_balance != null ? String(account.initial_balance) : '')
    setFeeEnabledScopes([])
    setFormFeeForms(emptyFeeRuleFormMap())
    setError(null)
  }, [account])

  useEffect(() => {
    if (!account?.id) return
    let cancelled = false
    fetchAccountFeeConfig(supabase, account.id)
      .then((config) => {
        if (cancelled) return
        const feeState = buildFeeStateFromRules(config.rules)
        setFeeEnabledScopes(feeState.enabledScopes)
        setFormFeeForms(feeState.forms)
      })
      .catch(() => {
        if (!cancelled) {
          setFeeEnabledScopes([])
          setFormFeeForms(emptyFeeRuleFormMap())
        }
      })
    return () => { cancelled = true }
  }, [account?.id])

  const handleSave = async () => {
    const trimmedName = name.trim()
    if (!trimmedName) {
      setError(t('accounts:nameRequired'))
      return
    }
    const trimmedPlatform = platform.trim()
    if (!trimmedPlatform) {
      setError(t('accounts:platformRequired'))
      return
    }
    if (!isEdit && maxAccounts !== null && accountCount >= maxAccounts) {
      setError(t('accounts:limitReached'))
      return
    }

    let parsedInitialBalance: number | null = null
    if (initialBalance.trim() !== '') {
      const val = parseFloat(initialBalance.replace(',', '.'))
      if (isNaN(val)) {
        setError(t('accounts:initialBalanceInvalid'))
        return
      }
      parsedInitialBalance = val
    }

    setSaving(true)
    setError(null)
    try {
      let accountId = account?.id
      if (isEdit && account) {
        const { error: updateError } = await supabase
          .from('accounts')
          .update({
            name: trimmedName,
            description: description.trim() || null,
            currency: formCurrency,
            platform: trimmedPlatform,
            initial_balance: parsedInitialBalance,
            updated_at: new Date().toISOString(),
          })
          .eq('id', account.id)
          .eq('user_id', userId)
        if (updateError) throw updateError
      } else {
        const { data: created, error: insertError } = await supabase.from('accounts').insert({
          user_id: userId,
          name: trimmedName,
          description: description.trim() || null,
          currency: formCurrency,
          platform: trimmedPlatform,
          initial_balance: parsedInitialBalance,
        }).select('id').single()
        if (insertError) throw insertError
        accountId = created.id
      }
      if (accountId) {
        await saveAccountFeeRules(supabase, accountId, feeEnabledScopes, formFeeForms)
      }
      onSaved()
      onClose()
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to save account')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="at-filter-modal-backdrop" role="dialog" aria-modal="true" onClick={onClose}>
      <div className="at-filter-modal at-cash-modal at-account-form-modal" onClick={(event) => event.stopPropagation()}>
        <header>
          <strong>{isEdit ? t('accounts:editAccount') : t('accounts:addAccount')}</strong>
          <button type="button" onClick={onClose} aria-label="Close"><Icon name="close" size={17} /></button>
        </header>
        <p>{t('accounts:subtitle')}</p>
        <div className="at-cash-modal-fields">
          <label>
            {t('accounts:name')}
            <input
              type="text"
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder={t('accounts:namePlaceholder')}
              autoFocus
            />
          </label>
          <label>
            {t('accounts:description')}
            <textarea
              value={description}
              onChange={(event) => setDescription(event.target.value)}
              placeholder={t('accounts:descriptionPlaceholder')}
              rows={3}
            />
          </label>
          <label>
            {t('accounts:currency')}
            <select value={formCurrency} onChange={(event) => setFormCurrency(event.target.value)}>
              {currencies.map((entry) => (
                <option key={entry.code} value={entry.code}>{entry.code} — {entry.name}</option>
              ))}
            </select>
          </label>
          <label>
            {t('accounts:platform')}
            <input
              type="text"
              value={platform}
              onChange={(event) => setPlatform(event.target.value)}
              placeholder={t('accounts:platformPlaceholder')}
            />
          </label>
          <label>
            {t('accounts:initialBalance')}
            <span className="at-field-optional">({t('accounts:optional')})</span>
            <span className="at-input-with-suffix">
              <input
                type="number"
                min="0"
                step="0.01"
                value={initialBalance}
                onChange={(event) => setInitialBalance(event.target.value)}
                placeholder={t('accounts:initialBalancePlaceholder')}
              />
              <span>{formCurrency}</span>
            </span>
            <small className="at-field-hint">{t('accounts:initialBalanceHint')}</small>
          </label>
          <AccountFeeRulesEditor
            enabledScopes={feeEnabledScopes}
            onEnabledScopesChange={setFeeEnabledScopes}
            forms={formFeeForms}
            onFormsChange={setFormFeeForms}
            currency={formCurrency}
            appearance="studio"
          />
        </div>
        {error && <p className="at-cash-modal-error">{error}</p>}
        <footer>
          <button type="button" onClick={onClose}>{t('buttons.cancel', { ns: 'common' })}</button>
          <button type="button" className="at-cash-modal-save" disabled={saving} onClick={handleSave}>
            {saving ? t('header.loading', { ns: 'common' }) : t('buttons.save', { ns: 'common' })}
          </button>
        </footer>
      </div>
    </div>
  )
}

function TestSubpage({
  section,
  trades,
  allTrades,
  portfolioTrades,
  range,
  accounts,
  transactions,
  selectedAccountId,
  strategies,
  metrics,
  chartData,
  currency,
  search,
  navigate,
  onAddTrade,
  onImport,
  onOpenTradeReview,
  getTradeDisplayPnl,
  latestRates,
  exchangeRates,
  onAccountCashSaved,
}: {
  section: string
  trades: DashboardTrade[]
  allTrades: DashboardTrade[]
  portfolioTrades: DashboardTrade[]
  range: RangeKey
  accounts: DashboardAccount[]
  transactions: AccountTransaction[]
  selectedAccountId: string | null
  strategies: DashboardStrategy[]
  metrics: DashboardMetrics
  chartData: { date: string; value: number; symbol: string }[]
  currency: string
  search: string
  navigate: (path: string) => void
  onAddTrade: () => void
  onImport: () => void
  onOpenTradeReview: (trade: DashboardTrade, event?: React.MouseEvent) => void
  getTradeDisplayPnl: (trade: DashboardTrade) => number
  latestRates: Map<string, number>
  exchangeRates: Map<string, number>
  onAccountCashSaved: () => void
}) {
  const { user } = useAuth()
  const { t } = useTranslation(['common', 'accounts'])
  const { showConfirm, showError } = useNotification()
  const { canUseAccounts, maxAccounts, limitsLoading } = useMembership()
  const [cashAccount, setCashAccount] = useState<DashboardAccount | null>(null)
  const [cashType, setCashType] = useState<ManualCashType>('deposit')
  const [showAccountForm, setShowAccountForm] = useState(false)
  const [editingAccount, setEditingAccount] = useState<DashboardAccount | null>(null)
  const [deletingAccountId, setDeletingAccountId] = useState<string | null>(null)

  const canAddAccount = !limitsLoading && canUseAccounts && (maxAccounts === null || accounts.length < maxAccounts)

  const openCreateAccountForm = () => {
    if (!canAddAccount) return
    setEditingAccount(null)
    setShowAccountForm(true)
  }

  const openEditAccountForm = (account: DashboardAccount) => {
    setEditingAccount(account)
    setShowAccountForm(true)
  }

  const handleDeleteAccount = async (account: DashboardAccount) => {
    if (!user) return
    const confirmed = await showConfirm({
      title: t('accounts:deleteTitle'),
      message: t('accounts:deleteConfirm'),
      confirmText: t('buttons.delete', { ns: 'common' }),
      cancelText: t('buttons.cancel', { ns: 'common' }),
      confirmButtonStyle: 'danger',
    })
    if (!confirmed) return
    try {
      setDeletingAccountId(account.id)
      const { error } = await supabase.from('accounts').delete().eq('id', account.id).eq('user_id', user.id)
      if (error) throw error
      if (selectedAccountId === account.id) navigate('/app-test/accounts')
      onAccountCashSaved()
    } catch (e: unknown) {
      showError(e instanceof Error ? e.message : 'Failed to delete account')
    } finally {
      setDeletingAccountId(null)
    }
  }

  useEffect(() => {
    if (section !== 'accounts') {
      setCashAccount(null)
      setShowAccountForm(false)
      setEditingAccount(null)
    }
  }, [section])

  const openCashModal = (account: DashboardAccount, type: ManualCashType) => {
    setCashAccount(account)
    setCashType(type)
  }
  const searchedAccounts = accounts.filter((account) =>
    !search.trim() || `${account.name} ${account.platform} ${account.currency}`.toLowerCase().includes(search.toLowerCase())
  )
  const searchedStrategies = strategies.filter((strategy) =>
    !search.trim() || `${strategy.display_name} ${strategy.description}`.toLowerCase().includes(search.toLowerCase())
  )

  if (section === 'trades') {
    return (
      <TradeJournal
        trades={trades}
        accounts={accounts}
        strategies={strategies}
        currency={currency}
        onAddTrade={onAddTrade}
        onImport={onImport}
        onOpenTradeReview={onOpenTradeReview}
        getTradeDisplayPnl={getTradeDisplayPnl}
      />
    )
  }

  if (section === 'analytics') {
    const processTrades = metrics.closed.map((trade) => ({
      ...trade,
      convertedPnl: getTradeDisplayPnl(trade),
      convertedFee: convertPnl(
        { pnl: trade.fee ?? 0, exit_date: trade.exit_date, currency: trade.currency },
        currency,
        exchangeRates
      ),
    }))
    return (
      <ProcessAnalysisSection
        trades={processTrades}
        currency={currency}
        formatMoney={formatMoney}
      />
    )
  }

  if (section === 'accounts') {
    const cashByAccount = transactions.reduce<Record<string, number>>((map, row) => {
      map[row.account_id] = (map[row.account_id] ?? 0) + Number(row.amount)
      return map
    }, {})
    const totalCash = accounts.reduce((sum, account) => {
      const cash = getAccountCashBalance(account, cashByAccount)
      return sum + convertBetweenCurrencies(cash, account.currency || currency, currency, currency, latestRates)
    }, 0)
    const totalAccountValue = accounts.reduce((sum, account) => {
      const equity = getAccountTotalValue(account, portfolioTrades, cashByAccount, currency, latestRates)
      return sum + convertBetweenCurrencies(equity, account.currency || currency, currency, currency, latestRates)
    }, 0)
    const selectedAccount = selectedAccountId ? accounts.find((account) => account.id === selectedAccountId) : null
    const ledgerRows = selectedAccount ? buildAccountLedgerRows(transactions.filter((row) => row.account_id === selectedAccount.id)) : []
    const selectedCurrency = selectedAccount?.currency || currency
    const selectedAccountCash = selectedAccount ? getAccountCashBalance(selectedAccount, cashByAccount) : 0
    const selectedAccountTotalValue = selectedAccount
      ? getAccountTotalValue(selectedAccount, portfolioTrades, cashByAccount, currency, latestRates)
      : 0
    const selectedAccountChartData = selectedAccount
      ? buildAccountDevelopmentChartData(
          transactions.filter((row) => row.account_id === selectedAccount.id),
          range,
          selectedAccountTotalValue,
        )
      : []

    return (
      <section className="at-subpage">
        {!selectedAccount && (
          <div className="at-subpage-kpis at-accounts-kpis">
            <MetricCard label="Total cash" context="All accounts" value={formatMoney(totalCash, currency, true)} progress={Math.min(Math.abs(totalCash) / 1000, 100)} tone="mint" footer="Ledger cash across accounts" />
            <MetricCard label="Total balance" context="Cash + open positions" value={formatMoney(totalAccountValue, currency, true)} progress={Math.min(Math.abs(totalAccountValue) / 1000, 100)} tone="blue" footer="Portfolio account value" />
          </div>
        )}
        {selectedAccount && (
          <article className="at-card at-performance-card at-account-detail-summary">
            <div className="at-card-title">
              <div>
                <span>Account balance</span>
                <div className="at-account-detail-metrics">
                  <p>
                    <span>Total cash</span>
                    <strong>{formatMoney(selectedAccountCash, selectedCurrency)}</strong>
                  </p>
                  <p>
                    <span>Total balance</span>
                    <strong>{formatMoney(selectedAccountTotalValue, selectedCurrency)}</strong>
                  </p>
                </div>
                <small>Cash + open positions · {range}</small>
              </div>
            </div>
            <div className="at-chart at-account-detail-chart">
              <StudioCumulativeAreaChart
                data={selectedAccountChartData}
                currency={selectedCurrency}
                gradientId="atAccountArea"
                tooltipLabel="Balance"
              />
            </div>
          </article>
        )}
        <article className="at-card at-subpage-panel">
          {selectedAccount ? (
            <>
              <div className="at-section-heading">
                <div>
                  <button className="at-ledger-back" onClick={() => navigate('/app-test/accounts')} type="button">
                    <Icon name="chevron" size={14} />
                    Accounts
                  </button>
                  <div>
                    <strong>{selectedAccount.name}</strong>
                    <span>{ledgerRows.length} cash movements · {selectedAccount.platform || 'Trading account'}</span>
                  </div>
                </div>
                <div className="at-section-heading-actions">
                  <button type="button" className="at-account-tool" onClick={() => openEditAccountForm(selectedAccount)} aria-label={t('buttons.edit', { ns: 'common' })}>
                    <Icon name="edit" size={14} />
                  </button>
                  <button
                    type="button"
                    className="at-account-tool danger"
                    disabled={deletingAccountId === selectedAccount.id}
                    onClick={() => handleDeleteAccount(selectedAccount)}
                    aria-label={t('buttons.delete', { ns: 'common' })}
                  >
                    <Icon name="trash" size={14} />
                  </button>
                </div>
              </div>
              {user && (
                <AccountLedgerTable
                  account={selectedAccount}
                  accountId={selectedAccount.id}
                  rows={ledgerRows}
                  accountCurrency={selectedCurrency}
                  allTrades={allTrades}
                  userId={user.id}
                  onOpenTradeReview={onOpenTradeReview}
                  onSaved={onAccountCashSaved}
                />
              )}
            </>
          ) : (
            <>
              <div className="at-section-heading">
                <div><strong>Account portfolio</strong><span>{searchedAccounts.length} accounts</span></div>
                <div className="at-section-heading-actions">
                  {canUseAccounts && (
                    <button type="button" className="at-primary-small" disabled={!canAddAccount} onClick={openCreateAccountForm}>
                      <Icon name="add" size={13} /> Add account
                    </button>
                  )}
                </div>
              </div>
              <div className="at-account-grid">
                {searchedAccounts.map((account) => {
                  const linkedTrades = allTrades.filter((trade) => trade.account_id === account.id)
                  const accountPnl = linkedTrades.filter((trade) => trade.exit_date).reduce((sum, trade) => sum + getTradeDisplayPnl(trade), 0)
                  const accountCurrency = account.currency || currency
                  const cash = getAccountCashBalance(account, cashByAccount)
                  const totalValue = getAccountTotalValue(account, portfolioTrades, cashByAccount, currency, latestRates)
                  return (
                    <div key={account.id} className="at-account-card">
                      <div className="at-account-card-head">
                        <button type="button" className="at-account-card-main at-account-card-title" onClick={() => navigate(`/app-test/accounts?account=${account.id}`)}>
                          <span className="at-account-icon"><Icon name="accounts" size={17} /></span>
                          <span className="at-account-card-copy"><strong>{account.name}</strong><small>{account.platform || 'Trading account'}</small></span>
                          <i className={account.is_active === false ? 'inactive' : ''}>{account.is_active === false ? 'Inactive' : 'Active'}</i>
                        </button>
                        <div className="at-account-card-tools">
                          <button type="button" className="at-account-tool" onClick={() => openEditAccountForm(account)} aria-label={t('buttons.edit', { ns: 'common' })}>
                            <Icon name="edit" size={14} />
                          </button>
                          <button
                            type="button"
                            className="at-account-tool danger"
                            disabled={deletingAccountId === account.id}
                            onClick={() => handleDeleteAccount(account)}
                            aria-label={t('buttons.delete', { ns: 'common' })}
                          >
                            <Icon name="trash" size={14} />
                          </button>
                        </div>
                      </div>
                      <button type="button" className="at-account-card-main" onClick={() => navigate(`/app-test/accounts?account=${account.id}`)}>
                        <div className="at-account-balances">
                          <p><span>Cash balance</span><strong>{formatMoney(cash, accountCurrency)}</strong></p>
                          <p><span>Total value</span><strong>{formatMoney(totalValue, accountCurrency)}</strong></p>
                        </div>
                        <div className="at-account-stats"><span><small>Range P/L</small><strong className={accountPnl >= 0 ? 'positive' : 'negative'}>{accountPnl >= 0 ? '+' : ''}{formatMoney(accountPnl, currency)}</strong></span><span><small>Trades</small><strong>{linkedTrades.length}</strong></span><span><small>Currency</small><strong>{accountCurrency}</strong></span></div>
                      </button>
                      <div className="at-account-actions">
                        <button type="button" className="at-account-action deposit" onClick={() => openCashModal(account, 'deposit')}>Deposit</button>
                        <button type="button" className="at-account-action withdraw" onClick={() => openCashModal(account, 'withdrawal')}>Withdraw</button>
                        <button type="button" className="at-account-action adjust" onClick={() => openCashModal(account, 'adjustment')}>Adjustment</button>
                      </div>
                    </div>
                  )
                })}
                {!searchedAccounts.length && (
                  <EmptyState
                    title={search.trim() ? 'No matching accounts' : 'No accounts yet'}
                    detail={search.trim() ? 'Create or search for another account.' : 'Add your first trading account to track cash and positions.'}
                    action={!search.trim() && canUseAccounts ? (
                      <button type="button" className="at-primary-small" disabled={!canAddAccount} onClick={openCreateAccountForm}>
                        <Icon name="add" size={13} /> Add account
                      </button>
                    ) : undefined}
                  />
                )}
              </div>
            </>
          )}
        </article>
        {cashAccount && user && (
          <AccountCashModal
            account={cashAccount}
            type={cashType}
            userId={user.id}
            onClose={() => setCashAccount(null)}
            onSaved={onAccountCashSaved}
          />
        )}
        {showAccountForm && user && (
          <AccountFormModal
            userId={user.id}
            account={editingAccount}
            accountCount={accounts.length}
            maxAccounts={maxAccounts}
            onClose={() => { setShowAccountForm(false); setEditingAccount(null) }}
            onSaved={onAccountCashSaved}
          />
        )}
      </section>
    )
  }

  if (section === 'strategies') {
    return (
      <section className="at-subpage">
        <div className="at-subpage-kpis">
          <MetricCard label="Strategies" context="Available playbooks" value={String(strategies.length)} progress={Math.min(strategies.length * 12, 100)} tone="blue" footer={`${strategies.filter((strategy) => strategy.is_active !== false).length} active strategies`} />
          <MetricCard label="Tagged trades" context="Selected range" value={String(allTrades.filter((trade) => trade.strategy_id).length)} progress={allTrades.length ? allTrades.filter((trade) => trade.strategy_id).length / allTrades.length * 100 : 0} tone="mint" footer="Trades linked to a strategy" />
          <MetricCard label="Average R:R" context="Completed trades" value={metrics.avgRR.toFixed(2)} progress={Math.min(metrics.avgRR * 25, 100)} tone="amber" footer={`${metrics.winRate.toFixed(1)}% overall win rate`} />
        </div>
        <article className="at-card at-subpage-panel">
          <div className="at-section-heading">
            <div><strong>Strategy library</strong><span>{searchedStrategies.length} playbooks</span></div>
            <button className="at-primary-small" onClick={() => navigate('/app/create-strategy')}><Icon name="add" size={13} /> New strategy</button>
          </div>
          <div className="at-strategy-list">
            {searchedStrategies.map((strategy) => {
              const strategyTrades = allTrades.filter((trade) => trade.strategy_id === strategy.id && trade.exit_date)
              const wins = strategyTrades.filter((trade) => Number(trade.pnl || 0) > 0).length
              const pnl = strategyTrades.reduce((sum, trade) => sum + getTradeDisplayPnl(trade), 0)
              const winRate = strategyTrades.length ? wins / strategyTrades.length * 100 : 0
              return (
                <button key={strategy.id} onClick={() => navigate(`/app/strategy/${encodeURIComponent(strategy.display_name)}`)}>
                  <span className="at-strategy-mark">{strategy.display_name.slice(0, 2).toUpperCase()}</span>
                  <span className="at-strategy-copy"><strong>{strategy.display_name}</strong><small>{strategy.description || 'No description added yet.'}</small></span>
                  <span><small>Trades</small><strong>{strategyTrades.length}</strong></span>
                  <span><small>Win rate</small><strong>{winRate.toFixed(0)}%</strong></span>
                  <span><small>Net P/L</small><strong className={pnl >= 0 ? 'positive' : 'negative'}>{pnl >= 0 ? '+' : ''}{formatMoney(pnl, currency, true)}</strong></span>
                  <i className={strategy.is_active === false ? 'inactive' : ''}>{strategy.is_public ? 'Public' : strategy.is_active === false ? 'Inactive' : 'Active'}</i>
                  <Icon name="chevron" size={14} />
                </button>
              )
            })}
            {!searchedStrategies.length && <EmptyState title="No matching strategies" detail="Create or search for another strategy." />}
          </div>
        </article>
      </section>
    )
  }

  return <Navigate to="/app-test" replace />
}

function MetricSelect({ value, onChange, compact = false }: { value: MetricId; onChange: (metric: MetricId) => void; compact?: boolean }) {
  return (
    <label className={`at-metric-select ${compact ? 'compact' : ''}`} title="Choose metric">
      <select value={value} onChange={(event) => onChange(event.target.value as MetricId)} aria-label="Choose dashboard metric">
        {METRIC_OPTIONS.map((option) => <option key={option.id} value={option.id}>{option.label}</option>)}
      </select>
      <span>⌄</span>
    </label>
  )
}

interface TestFilterOptions {
  strategies: DashboardStrategy[]
  accounts: DashboardAccount[]
  markets: string[]
  exchanges: string[]
  tags: string[]
}

type MultiFilterField = 'strategyIds' | 'accountIds' | 'markets' | 'exchanges' | 'tags'

function FilterChoiceGroup({
  label,
  choices,
  selected,
  onToggle,
}: {
  label: string
  choices: { id: string; label: string }[]
  selected: string[]
  onToggle: (id: string) => void
}) {
  return (
    <fieldset className="at-filter-choice-group">
      <legend>{label}<small>{selected.length ? `${selected.length} selected` : 'All'}</small></legend>
      <div>
        {choices.length ? choices.map((choice) => (
          <label key={choice.id}>
            <input type="checkbox" checked={selected.includes(choice.id)} onChange={() => onToggle(choice.id)} />
            <span>{choice.label}</span>
          </label>
        )) : <p>No options</p>}
      </div>
    </fieldset>
  )
}

function WidgetFilterControl({
  value,
  onChange,
  options,
}: {
  value: TestWidgetFilter
  onChange: (filter: TestWidgetFilter) => void
  options: TestFilterOptions
}) {
  const activeCount = value.strategyIds.length + value.accountIds.length + value.markets.length + value.exchanges.length + value.tags.length
  const toggleValue = (field: MultiFilterField, id: string) => {
    const current = value[field]
    onChange({
      ...value,
      [field]: current.includes(id) ? current.filter((item) => item !== id) : [...current, id],
    })
  }
  return (
    <details className="at-widget-filter">
      <summary className={activeCount ? 'active' : ''} title="Widget filters" aria-label={`${activeCount} active widget filters`}>
        <Icon name="filter" size={12} />
        {activeCount > 0 && <b>{activeCount}</b>}
      </summary>
      <div className="at-widget-filter-panel">
        <div><strong>Widget filters</strong>{activeCount > 0 && <button type="button" onClick={() => onChange(EMPTY_WIDGET_FILTER)}>Reset</button>}</div>
        <FilterChoiceGroup label="Strategies" choices={options.strategies.map((strategy) => ({ id: strategy.id, label: strategy.display_name }))} selected={value.strategyIds} onToggle={(id) => toggleValue('strategyIds', id)} />
        <FilterChoiceGroup label="Accounts" choices={options.accounts.map((account) => ({ id: account.id, label: account.name }))} selected={value.accountIds} onToggle={(id) => toggleValue('accountIds', id)} />
        <FilterChoiceGroup label="Markets" choices={options.markets.map((market) => ({ id: market, label: market }))} selected={value.markets} onToggle={(id) => toggleValue('markets', id)} />
        <FilterChoiceGroup label="Exchanges" choices={options.exchanges.map((exchange) => ({ id: exchange, label: exchange }))} selected={value.exchanges} onToggle={(id) => toggleValue('exchanges', id)} />
        <FilterChoiceGroup label="Tags" choices={options.tags.map((tag) => ({ id: tag, label: tag }))} selected={value.tags} onToggle={(id) => toggleValue('tags', id)} />
      </div>
    </details>
  )
}

function MetricCard({
  label,
  context,
  value,
  progress,
  tone,
  footer,
  selectedMetric,
  onMetricChange,
  filter,
  onFilterChange,
  filterOptions,
}: {
  label: string
  context: string
  value: string
  progress: number
  tone: string
  footer: string
  selectedMetric?: MetricId
  onMetricChange?: (metric: MetricId) => void
  filter?: TestWidgetFilter
  onFilterChange?: (filter: TestWidgetFilter) => void
  filterOptions?: TestFilterOptions
}) {
  return (
    <article className="at-metric-card">
      <div>
        {selectedMetric && onMetricChange
          ? <MetricSelect value={selectedMetric} onChange={onMetricChange} />
          : <span>{label}</span>}
        <span className="at-metric-card-tools">
          <small>{context}</small>
          {filter && onFilterChange && filterOptions && <WidgetFilterControl value={filter} onChange={onFilterChange} options={filterOptions} />}
        </span>
      </div>
      <strong>{value}</strong>
      <div className={`at-segment-progress ${tone}`} style={{ '--progress': `${Math.max(4, Math.min(progress, 100))}%` } as React.CSSProperties}><i /></div>
      <p>{footer}</p>
    </article>
  )
}

function MiniMetricCard({
  metric,
  selectedMetric,
  onMetricChange,
  filter,
  onFilterChange,
  filterOptions,
}: {
  metric: { label: string; context: string; value: string; footer: string }
  selectedMetric: MetricId
  onMetricChange: (metric: MetricId) => void
  filter: TestWidgetFilter
  onFilterChange: (filter: TestWidgetFilter) => void
  filterOptions: TestFilterOptions
}) {
  return (
    <div className="at-mini-metric">
      <div className="at-mini-metric-head">
        <MetricSelect value={selectedMetric} onChange={onMetricChange} compact />
        <WidgetFilterControl value={filter} onChange={onFilterChange} options={filterOptions} />
      </div>
      <strong>{metric.value}</strong>
      <small>{metric.footer}</small>
    </div>
  )
}

function EmptyState({ title, detail, action }: { title: string; detail: string; action?: ReactNode }) {
  return (
    <div className="at-empty">
      <span>◇</span>
      <strong>{title}</strong>
      <p>{detail}</p>
      {action}
    </div>
  )
}

export default AppTest
